import { useState, useEffect, useRef, useCallback } from "react";
import shp from "shpjs";
import IrrigationCalc from "./IrrigationCalc";
import Dashboard from "./Dashboard";
import { MapContainer, TileLayer, WMSTileLayer, FeatureGroup, useMap, useMapEvents, CircleMarker, Polyline } from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import L from "leaflet";
import axios from "axios";
import { useTranslation } from "react-i18next";
import i18n from "./i18n/i18n.js";
import { tCrop } from "./cropNames.js";
import DiseaseModal from "./DiseaseModal.js";
import { printFieldReport } from "./printReport.js";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";

// Canonical 96-crop list — display names matching IRRIG_CROPS in IrrigationCalc.js
// and _CROP_REQUIREMENTS in backend. Single source of truth across the platform.
const CROPS = [
  // Vegetables
  "Artichoke","Asparagus","Beet (table)","Broccoli","Brussels Sprouts","Cabbage",
  "Cantaloupe","Carrot","Cauliflower","Celery","Cucumber","Eggplant","Garlic","Kale",
  "Leek","Lettuce","Okra","Onion (dry)","Onion (green)","Peas (fresh)","Sweet Pepper",
  "Pumpkin / Winter Squash","Radish","Spinach","Squash (summer/zucchini)","Sweet Corn",
  "Swiss Chard","Tomato (fresh)","Tomato (processing)","Turnip","Watermelon",
  // Root/Tubers
  "Cassava","Potato","Sweet Potato","Yam",
  // Legumes
  "Bean (dry)","Bean (green/snap)","Chickpea","Cowpea","Fava Bean",
  "Groundnut / Peanut","Lentil","Soybean",
  // Cereals
  "Barley","Corn / Maize","Millet","Oat","Quinoa","Rice (paddy)","Rye",
  "Sorghum","Wheat (winter)","Wheat (spring)",
  // Oil Crops
  "Canola / Rapeseed","Cotton","Flax / Linseed","Safflower","Sesame","Sunflower",
  // Sugar
  "Sugarcane","Sugar Beet",
  // Tropical Fruits
  "Avocado","Banana","Citrus (orange)","Citrus (lemon/lime)","Coconut","Coffee",
  "Mango","Papaya","Pineapple","Tea",
  // Deciduous Fruits / Nuts
  "Almond","Apple","Apricot","Cherry","Olive","Peach / Nectarine","Pear",
  "Pistachio","Plum / Prune","Walnut",
  // Berries / Vines
  "Blueberry","Grape (table)","Grape (wine)","Raspberry","Strawberry",
  // Forages
  "Alfalfa (multi-cut)","Bermuda Grass","Clover","Ryegrass / Pasture",
  // Specialty / Herbs
  "Basil","Cilantro / Coriander","Hemp / Cannabis","Hops","Mint","Tobacco",
];

const LANGUAGES = [
  { code:"en", label:"EN",  name:"English" },
  { code:"zh", label:"中文", name:"Chinese" },
  { code:"hi", label:"हिं", name:"Hindi" },
  { code:"es", label:"ES",  name:"Spanish" },
  { code:"ar", label:"عر",  name:"Arabic" },
  { code:"fr", label:"FR",  name:"French" },
  { code:"sw", label:"SW",  name:"Swahili" },
];

// Raster overlays. type="wms" uses WMSTileLayer; type="xyz" uses TileLayer.
const RASTER_LAYERS = [
  {
    id: "elevation",
    label: "Elevation (OpenTopoMap)",
    category: "Terrain",
    type: "xyz",
    url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
    attribution: "© OpenTopoMap contributors",
    opacity: 0.82,
    maxZoom: 17,
    legend: {
      type: "gradient",
      colors: ["#aad3df","#78b69e","#c8b560","#a08040","#808080","#ffffff"],
      labels: ["Water","Low","Mid","Hill","Peak","Snow"],
    },
  },
  {
    id: "soilgrids_ph",
    label: "Soil pH (SoilGrids)",
    category: "Soil",
    type: "wms",
    url: "https://maps.isric.org/mapserv?map=/map/phh2o.map",
    wmsLayers: "phh2o_0-5cm_mean",
    attribution: "© ISRIC SoilGrids (CC-BY 4.0)",
    opacity: 0.85,
    maxZoom: 18,
    legend: { type:"gradient", colors:["#e4f542","#b8e020","#78c832","#2d8c20","#009980","#00bcd4"], domain:[3.5,9.2], unit:"pH" },
  },
  {
    id: "soilgrids_oc",
    label: "Organic Carbon (SoilGrids)",
    category: "Soil",
    type: "wms",
    url: "https://maps.isric.org/mapserv?map=/map/soc.map",
    wmsLayers: "soc_0-5cm_mean",
    attribution: "© ISRIC SoilGrids (CC-BY 4.0)",
    opacity: 0.85,
    maxZoom: 18,
    legend: { type:"gradient", colors:["#f0eae8","#d4bab5","#b89088","#a07868","#a07858"], domain:[0,150], unit:"g/kg" },
  },
  {
    id: "soilgrids_nitrogen",
    label: "Total Nitrogen (SoilGrids)",
    category: "Soil",
    type: "wms",
    url: "https://maps.isric.org/mapserv?map=/map/nitrogen.map",
    wmsLayers: "nitrogen_0-5cm_mean",
    attribution: "© ISRIC SoilGrids (CC-BY 4.0)",
    opacity: 0.85,
    maxZoom: 18,
    legend: { type:"gradient", colors:["#f9fcf8","#eef8ec","#aadba9","#53a773","#286a50"], domain:[0,3000], unit:"mg/kg" },
  },
  {
    id: "soilgrids_clay",
    label: "Clay Content (SoilGrids)",
    category: "Soil",
    type: "wms",
    url: "https://maps.isric.org/mapserv?map=/map/clay.map",
    wmsLayers: "clay_0-5cm_mean",
    attribution: "© ISRIC SoilGrids (CC-BY 4.0)",
    opacity: 0.85,
    maxZoom: 18,
    legend: { type:"gradient", colors:["#f6e6d5","#e8d0b8","#cfa881","#b88860","#a28261"], domain:[0,80], unit:"% clay" },
  },
  // NASA GIBS WMS layers
  {
    id: "modis_ndvi",
    label: "NDVI (NASA MODIS)",
    category: "Vegetation",
    type: "wms",
    url: "https://gibs.earthdata.nasa.gov/wms/epsg3857/best/wms.cgi",
    wmsLayers: "MODIS_Terra_NDVI_8Day",
    wmsTime: "2025-09-01",
    attribution: "NASA GIBS / MODIS Terra",
    opacity: 0.88,
    maxZoom: 18,
    legend: { type:"gradient", colors:["#f1ecec","#b19883","#6eaa00","#057800","#003c00"], domain:[-0.1,0.8], unit:"NDVI" },
  },
  {
    id: "modis_lst",
    label: "Land Surface Temp (NASA MODIS)",
    category: "Climate",
    type: "wms",
    url: "https://gibs.earthdata.nasa.gov/wms/epsg3857/best/wms.cgi",
    wmsLayers: "MODIS_Terra_L3_Land_Surface_Temp_8Day_Day",
    wmsTime: "2025-09-01",
    attribution: "NASA GIBS / MODIS Terra",
    opacity: 0.88,
    maxZoom: 18,
    legend: { type:"gradient", colors:["#c900ff","#0100ff","#04fff7","#bdff00","#ff9d00","#ff0400"], domain:["-40°C","0°C","60°C"], unit:"Temp" },
  },
  {
    id: "modis_fires",
    label: "Active Fires (NASA FIRMS)",
    category: "Climate",
    type: "xyz",
    url: "https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/FIRMS_VIIRS_Day/default/2024-09-01/GoogleMapsCompatible_Level8/{z}/{y}/{x}.png",
    attribution: "NASA FIRMS / VIIRS",
    opacity: 0.9,
    maxZoom: 8,
    maxNativeZoom: 8,
    legend: {
      type: "categorical",
      classes: [
        { color: "#ff4500", label: "Active fire" },
        { color: "#ff8c00", label: "High confidence" },
      ],
    },
  },
  {
    id: "worldcover",
    label: "Land Cover (NASA MODIS)",
    category: "Vegetation",
    type: "wms",
    url: "https://gibs.earthdata.nasa.gov/wms/epsg3857/best/wms.cgi",
    wmsLayers: "MODIS_Combined_L3_IGBP_Land_Cover_Type_Annual",
    wmsTime: "2023-01-01",
    attribution: "NASA GIBS / MODIS Terra Land Cover",
    opacity: 0.82,
    maxZoom: 18,
    legend: { type:"categorical", classes:[
      { color:"rgb(33,138,33)",   label:"Evergreen Needleleaf Forest" },
      { color:"rgb(49,204,49)",   label:"Evergreen Broadleaf Forest" },
      { color:"rgb(152,204,49)",  label:"Deciduous Needleleaf Forest" },
      { color:"rgb(150,250,150)", label:"Deciduous Broadleaf Forest" },
      { color:"rgb(141,186,141)", label:"Mixed Forest" },
      { color:"rgb(186,141,141)", label:"Closed Shrublands" },
      { color:"rgb(245,222,179)", label:"Open Shrublands" },
      { color:"rgb(218,235,157)", label:"Woody Savannas" },
      { color:"rgb(255,213,0)",   label:"Savannas" },
      { color:"rgb(240,185,103)", label:"Grasslands" },
      { color:"rgb(71,131,181)",  label:"Permanent Wetlands" },
      { color:"rgb(250,239,115)", label:"Croplands" },
      { color:"rgb(255,0,0)",     label:"Urban and Built-up" },
      { color:"rgb(153,147,86)",  label:"Cropland/Veg. Mosaic" },
      { color:"rgb(255,255,255)", label:"Permanent Snow / Ice" },
      { color:"rgb(191,191,189)", label:"Barren" },
      { color:"rgb(134,202,227)", label:"Water Bodies" },
    ]},
  },
];

const MAP_LAYERS = {
  satellite: { label:"Satellite",   url:"https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}", attribution:"© Google", maxZoom:21, overlay:null },
  labels:    { label:"Sat+Labels",  url:"https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}", attribution:"© Google", maxZoom:21, overlay:null },
  osm:       { label:"Map",         url:"https://tile.openstreetmap.org/{z}/{x}/{y}.png", attribution:"© OpenStreetMap (ODbL)", maxZoom:19, overlay:null },
};

function getClimateAdvice(koppen, crop) {
  const c = crop.toLowerCase();
  const advisories = {
    Af:  { good:["rice","banana","cassava","oil palm","cocoa","coffee","rubber","coconut","pineapple","papaya"], warn:["wheat","potato","barley","oats"], text:"Tropical rainforest climate. Year-round warmth and rainfall favor tropical crops. High fungal and bacterial disease pressure year-round." },
    Am:  { good:["rice","sugarcane","banana","cassava","maize","groundnut"], warn:["wheat","potato","barley"], text:"Tropical monsoon climate. Strong wet season drives rapid crop growth. Waterlogging and blast disease risk during peak rains." },
    Aw:  { good:["maize","sorghum","millet","cowpea","groundnut","cotton","cassava"], warn:["rice","potato"], text:"Tropical savanna climate. Distinct wet and dry seasons. Drought-tolerant crops excel. Fall armyworm and aflatoxin are significant risks." },
    BSh: { good:["sorghum","millet","groundnut","cowpea","cotton","sesame"], warn:["rice","potato","coffee"], text:"Hot semi-arid steppe. Water is the primary limiting factor. Drought-tolerant varieties essential." },
    BSk: { good:["wheat","barley","sorghum","sunflower","lentil","chickpea"], warn:["rice","banana","cocoa"], text:"Cold semi-arid steppe. Cereals and legumes perform well in spring. Frost risk in shoulder seasons." },
    BWh: { good:["date palm","cotton"], warn:["rice","potato","coffee","banana"], text:"Hot desert climate. Irrigated cultivation only. Salinity risk from irrigation water." },
    Cfa: { good:["maize","soybean","cotton","rice","wheat","sugarcane","tomato","grape"], warn:["potato"], text:"Humid subtropical climate. Wide crop range. High humidity increases fungal disease pressure in summer." },
    Cfb: { good:["wheat","potato","barley","oats","apple","strawberry","grape","cabbage","carrot"], warn:["rice","banana","sugarcane","cocoa"], text:"Oceanic climate. Ideal for temperate vegetables and cereals. Late blight is the dominant potato risk." },
    Csa: { good:["wheat","olive","grape","tomato","orange","lemon","sunflower","barley"], warn:["rice","banana","cocoa"], text:"Mediterranean climate. Hot dry summers. Summer irrigation required. Powdery mildew and drought are key risks." },
    Dfa: { good:["maize","soybean","wheat","sunflower","potato"], warn:["rice","banana","cocoa","sugarcane"], text:"Humid continental climate. Cold winters, spring flooding risk. Sclerotinia and rust are common." },
    Dfb: { good:["wheat","barley","potato","oats","rye","cabbage","carrot"], warn:["maize","rice","banana","sugarcane"], text:"Continental subarctic. Short growing season. Hardy cereals and root vegetables dominate." },
  };
  const entry = advisories[koppen] || { good:[], warn:[], text:"Temperate climate with moderate conditions." };
  let suitability="Moderate", suitColor="#EF9F27";
  if (entry.good.some(g=>c.includes(g)||g.includes(c))) { suitability="Well suited"; suitColor="#639922"; }
  if (entry.warn.some(w=>c.includes(w)||w.includes(c))) { suitability="Challenging"; suitColor="#E24B4A"; }
  return { text:entry.text, suitability, suitColor };
}

const RISK_COLORS = { High:"#E24B4A", Medium:"#EF9F27", Low:"#639922" };

function LegendImg({ src, alt, apiBase }) {
  const [err, setErr] = useState(false);
  const proxied = `${apiBase}/legend-proxy?url=${encodeURIComponent(src)}`;
  if (err) return <span style={{ fontSize:9, color:"#aaa" }}>Legend unavailable</span>;
  return <img src={proxied} alt={alt} onError={() => setErr(true)} style={{ maxWidth:"100%", display:"block" }} />;
}

function RasterLegends({ activeRasters, apiBase, style }) {
  const active = RASTER_LAYERS.filter(r => activeRasters[r.id] && (r.legendUrl || r.legend));
  if (!active.length) return null;
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:6, alignItems:"flex-end", pointerEvents:"none", ...style }}>
      {active.map(r => (
        <div key={r.id} style={{ background:"rgba(255,255,255,0.96)", backdropFilter:"blur(4px)", padding:"7px 10px", borderRadius:8, border:"0.5px solid #ddd", boxShadow:"0 2px 8px rgba(0,0,0,0.15)", maxWidth:200, pointerEvents:"auto" }}>
          <div style={{ fontSize:10, fontWeight:700, color:"#333", marginBottom:5, lineHeight:1.3 }}>{r.label}</div>
          {r.legendUrl ? (
            <LegendImg src={r.legendUrl} alt={r.label} apiBase={apiBase} />
          ) : r.legend.type === "gradient" ? (
            <>
              <div style={{ display:"flex", height:9, borderRadius:4, overflow:"hidden", width:"100%" }}>
                {r.legend.colors.map((c,i) => <div key={i} style={{ flex:1, background:c }}/>)}
              </div>
              {(r.legend.domain || r.legend.labels) && (
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:9, color:"#666", marginTop:3 }}>
                  {r.legend.domain ? (
                    Array.isArray(r.legend.domain) && typeof r.legend.domain[0] === "string"
                      ? r.legend.domain.map((l,i) => <span key={i}>{l}</span>)
                      : <><span>{r.legend.domain[0]}</span><span style={{color:"#999"}}>{r.legend.unit}</span><span>{r.legend.domain[1]}</span></>
                  ) : r.legend.labels.map((l,i) => <span key={i}>{l}</span>)}
                </div>
              )}
            </>
          ) : r.legend?.classes ? (
            <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
              {r.legend.classes.map((cls,i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:5 }}>
                  <div style={{ width:10, height:10, borderRadius:2, background:cls.color, flexShrink:0 }}/>
                  <span style={{ fontSize:9, color:"#444", lineHeight:1.2 }}>{cls.label}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}
const round = (v,d) => v!=null ? Math.round(v*Math.pow(10,d))/Math.pow(10,d) : null;


function healthColor(s) { return { "Healthy":"#639922","Fair":"#EF9F27","Moderate stress":"#E8830A","Severe stress":"#E24B4A" }[s]||"#888"; }
function ndmiColor(v)   { if(v==null)return"#888"; if(v<-0.2)return"#E24B4A"; if(v<0)return"#EF9F27"; if(v<0.2)return"#639922"; return"#2196F3"; }
function ndreColor(s)   { return { "Deficient":"#E24B4A","Moderate":"#EF9F27","Sufficient":"#639922","Unknown":"#888" }[s]||"#888"; }

// Uses Leaflet's built-in locate() with useMapEvents (survives StrictMode double-mount)
function LocateOnMap({ triggerRef, flyRef }) {
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  const hasFlownToUser = useRef(false); // only auto-center on user once

  const dotIcon = L.divIcon({
    className: "",
    html: `<div style="width:16px;height:16px;border-radius:50%;background:rgba(33,150,243,0.9);border:2.5px solid #fff;box-shadow:0 0 0 4px rgba(33,150,243,0.25);"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });

  const map = useMapEvents({
    locationfound(e) {
      // Place dot + accuracy circle
      if (markerRef.current) { markerRef.current.remove(); circleRef.current.remove(); }
      markerRef.current = L.marker(e.latlng, { icon: dotIcon }).addTo(map);
      circleRef.current = L.circle(e.latlng, {
        radius: e.accuracy / 2, color: "#2196F3",
        fillColor: "#2196F3", fillOpacity: 0.1, weight: 1,
      }).addTo(map);
      // Cache location so next page load opens here instantly
      try { sessionStorage.setItem("of_last_loc", JSON.stringify([e.latlng.lat, e.latlng.lng])); } catch {}
      // First fix: jump immediately (no animation) so there's no "fly from Limuru" effect
      if (!hasFlownToUser.current) {
        hasFlownToUser.current = true;
        map.setView(e.latlng, 15);
      }
    },
    locationerror(e) {
      console.warn("Geolocation error:", e.message);
    },
  });

  useEffect(() => {
    // locate button re-enables fly-to-user for that one press
    triggerRef.current = () => {
      hasFlownToUser.current = false;
      map.locate({ enableHighAccuracy: true });
    };
    flyRef.current = (lat, lng, zoom=14) => map.flyTo([lat, lng], zoom, { animate: true, duration: 1.2 });
    flyRef.fitBounds = (bounds) => map.fitBounds(bounds, { padding: [40, 40], animate: true, duration: 1.0 });
    // auto-locate on mount (no setView — handled in locationfound)
    map.locate({ enableHighAccuracy: true });
  }, [map, triggerRef, flyRef]);

  return null;
}

function TouchPolygonDrawer({ onComplete, onStartDrawing, isMobile }) {
  const [points, setPoints] = useState([]);
  const [drawing, setDrawing] = useState(false);
  const map = useMap();
  
  const drawBtnRef = useCallback((node) => {
    if (node) L.DomEvent.disableClickPropagation(node);
  }, []);

  const activePanelRef = useCallback((node) => {
    if (node) L.DomEvent.disableClickPropagation(node);
  }, []);

  useEffect(() => {
    if (!isMobile || !drawing) return;
    const handler = (e) => {
      setPoints(prev => [...prev, [e.latlng.lng, e.latlng.lat]]);
    };
    map.on('click', handler);
    return () => map.off('click', handler);
  }, [isMobile, drawing, map]);

  function finish() {
    if (points.length >= 3) {
      onComplete(points);
    }
    setPoints([]);
    setDrawing(false);
  }

  function cancel() {
    setPoints([]);
    setDrawing(false);
  }

  if (!isMobile) return null;

  return (
    <>
      {points.map((pt, i) => (
        <CircleMarker
          key={i}
          center={[pt[1], pt[0]]}
          radius={6}
          pathOptions={{ color: '#2d5a1b', fillColor: '#7AB648', fillOpacity: 1, weight: 2 }}
        />
      ))}
      {points.length >= 2 && (
        <Polyline
          positions={[...points.map(p => [p[1], p[0]]), [points[0][1], points[0][0]]]}
          pathOptions={{ color: '#7AB648', weight: 2, dashArray: '6 4' }}
        />
      )}

      {!drawing ? (
        <div
          ref={drawBtnRef}
          style={{
            position: 'absolute', top: 80, right: 12, zIndex: 1000,
            background: '#1a2e1a', color: '#fff', border: 'none',
            borderRadius: 8, padding: '10px 14px', fontSize: 13,
            fontWeight: 600, cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
          }}
          onClick={() => {
            setDrawing(true);
            if (onStartDrawing) onStartDrawing();
          }}
        >
          Draw Field
        </div>
      ) : (
        <div
          ref={activePanelRef}
          style={{
            position: 'absolute', top: 80, right: 12, zIndex: 1000,
            display: 'flex', flexDirection: 'column', gap: 8
          }}
        >
          <div style={{
            background: '#1a2e1a', color: '#7AB648', borderRadius: 8,
            padding: '8px 12px', fontSize: 12, fontWeight: 600,
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)', textAlign: 'center'
          }}>
            Tap to place points<br/>
            <span style={{ color: '#aaa', fontWeight: 400 }}>{points.length} placed</span>
          </div>
          {points.length >= 3 && (
            <div
              onClick={(e) => { e.stopPropagation(); finish(); }}
              style={{
                background: '#2d5a1b', color: '#fff', borderRadius: 8,
                padding: '10px 14px', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.3)', textAlign: 'center'
              }}
            >
              Done ({points.length} pts)
            </div>
          )}
          <div
            onClick={(e) => { e.stopPropagation(); cancel(); }}
            style={{
              background: '#333', color: '#aaa', borderRadius: 8,
              padding: '8px 14px', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.3)', textAlign: 'center'
            }}
          >
            Cancel
          </div>
        </div>
      )}
    </>
  );
}

// ── Area Analysis Results Panel ──────────────────────────────────────────────
function AreaAnalysisPanel({ data, lang }) {
  const [showAll, setShowAll] = useState(false);
  const crops = showAll ? data.all_crops : data.top_crops;
  const scoreColor = s => s >= 75 ? "#15803d" : s >= 50 ? "#854d0e" : s >= 30 ? "#b45309" : "#991b1b";
  const statusColor = { "Well-watered":"#15803d","Adequate":"#2563eb","Moderate stress":"#b45309","Water stressed":"#991b1b","Cool":"#2563eb","Optimal":"#15803d","Warm":"#b45309","Hot":"#991b1b" };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:10, marginTop:4 }}>

      {/* Climate normals source citation */}
      {data.climate_normals && (
        <div style={{ background:"#f5f9f3", borderRadius:10, border:"1px solid #d4e8c8", padding:"10px 14px", fontSize:11, color:"#3a6b2a" }}>
          <strong>Data sources:</strong> Crop scoring — {data.climate_normals.source} (avg {data.climate_normals.annual_mean_temp_c}°C
          {data.climate_normals.coldest_month_c != null ? `, coldest month ${data.climate_normals.coldest_month_c}°C` : ""}
          {data.climate_normals.hottest_month_c != null ? `, hottest ${data.climate_normals.hottest_month_c}°C` : ""}
          {data.climate_normals.annual_rain_mm ? `, ${Math.round(data.climate_normals.annual_rain_mm)}mm/yr rainfall` : ""}).
          {" "}Climate resilience — Köppen-Geiger projections (SSP2-4.5 / SSP5-8.5, 2041–2085).
        </div>
      )}

      {/* Top crop rankings */}
      <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e0e8d8", padding:"14px 16px" }}>
        <div style={{ fontSize:9, fontWeight:700, color:"#999", textTransform:"uppercase", letterSpacing:1, marginBottom:10 }}>
          Crop Suitability Ranking
        </div>
        {crops.map((c, i) => (
          <div key={c.crop} style={{ display:"flex", alignItems:"center", gap:10, marginBottom: i < crops.length - 1 ? 8 : 0 }}>
            <div style={{ width:20, fontSize:11, fontWeight:700, color:"#aaa", textAlign:"right" }}>#{i+1}</div>
            <div style={{ flex:1 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:3 }}>
                <span style={{ fontSize:13, fontWeight:600, color:"#1a2e1a" }}>{tCrop(c.crop, lang)}</span>
                <span style={{ fontSize:12, fontWeight:700, color: scoreColor(c.score) }}>{c.score}%</span>
              </div>
              <div style={{ height:5, borderRadius:3, background:"#e8f0e0", overflow:"hidden" }}>
                <div style={{ height:"100%", borderRadius:3, width:`${c.score}%`, background: scoreColor(c.score), transition:"width 0.5s" }} />
              </div>
              {c.reasons.length > 0 && (
                <div style={{ fontSize:10, marginTop:2, display:"flex", flexWrap:"wrap", gap:"2px 6px" }}>
                  {c.reasons.map((r, ri) => (
                    <span key={ri} style={{ color: r.includes("✓") ? "#7a9a6a" : "#b45309" }}>{r}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        <button onClick={() => setShowAll(v => !v)}
          style={{ marginTop:10, fontSize:11, color:"#2d5a1b", background:"none", border:"none", cursor:"pointer", padding:0, fontWeight:600 }}>
          {showAll ? "Show top 10 ▲" : `Show all ${data.all_crops.length} crops ▼`}
        </button>
      </div>

      {/* Environmental summary cards */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
        {/* Water */}
        <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e0e8d8", padding:"12px 14px" }}>
          <div style={{ fontSize:9, fontWeight:700, color:"#999", textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>Water</div>
          <div style={{ fontSize:15, fontWeight:700, color: statusColor[data.water?.status] || "#1a2e1a" }}>{data.water?.status || "—"}</div>
          {data.water?.ndwi != null
            ? <div style={{ fontSize:11, color:"#7a9a6a" }}>NDWI {data.water.ndwi} (satellite)</div>
            : data.water?.annual_rainfall_mm > 0 && <div style={{ fontSize:11, color:"#aaa" }}>based on {Math.round(data.water.annual_rainfall_mm)}mm/yr rainfall</div>}
          {data.water?.annual_rainfall_mm > 0 && <div style={{ fontSize:11, color:"#7a9a6a" }}>~{Math.round(data.water.annual_rainfall_mm)} mm/yr</div>}
        </div>
        {/* Heat */}
        <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e0e8d8", padding:"12px 14px" }}>
          <div style={{ fontSize:9, fontWeight:700, color:"#999", textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>Heat</div>
          <div style={{ fontSize:15, fontWeight:700, color: statusColor[data.heat?.status] || "#1a2e1a" }}>{data.heat?.status || "—"}</div>
          {data.heat?.lst_celsius != null
            ? <div style={{ fontSize:11, color:"#7a9a6a" }}>LST {data.heat.lst_celsius}°C (satellite)</div>
            : data.heat?.mean_temp_c != null && <div style={{ fontSize:11, color:"#aaa" }}>from climate normals</div>}
          {data.heat?.mean_temp_c != null && <div style={{ fontSize:11, color:"#7a9a6a" }}>~{data.heat.mean_temp_c}°C avg</div>}
        </div>
        {/* Vegetation */}
        <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e0e8d8", padding:"12px 14px" }}>
          <div style={{ fontSize:9, fontWeight:700, color:"#999", textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>Vegetation</div>
          {data.vegetation?.status
            ? <>
                <div style={{ fontSize:13, fontWeight:700, color:"#1a2e1a" }}>{data.vegetation.status}</div>
                <div style={{ fontSize:11, color:"#7a9a6a" }}>NDVI {data.vegetation.ndvi} (satellite)</div>
                {data.vegetation?.ndre != null && <div style={{ fontSize:11, color:"#7a9a6a" }}>NDRE {data.vegetation.ndre}</div>}
              </>
            : <>
                <div style={{ fontSize:13, fontWeight:700, color:"#bbb" }}>No satellite data</div>
                <div style={{ fontSize:11, color:"#aaa", marginTop:3 }}>Draw a field to get NDVI</div>
              </>}
        </div>
        {/* Soil */}
        <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e0e8d8", padding:"12px 14px" }}>
          <div style={{ fontSize:9, fontWeight:700, color:"#999", textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>Soil</div>
          {data.soil?.ph != null
            ? <div style={{ fontSize:13, fontWeight:700, color:"#1a2e1a" }}>pH {data.soil.ph}</div>
            : <div style={{ fontSize:13, fontWeight:700, color:"#bbb" }}>No soil data</div>}
          {data.soil?.texture_class && <div style={{ fontSize:11, color:"#7a9a6a" }}>{data.soil.texture_class}</div>}
          {data.soil?.elevation_m != null && <div style={{ fontSize:11, color:"#7a9a6a" }}>{data.soil.elevation_m}m elev</div>}
          {data.soil?.slope_pct != null && <div style={{ fontSize:11, color:"#7a9a6a" }}>{data.soil.slope_pct}% slope</div>}
        </div>
      </div>

      {/* Soil fertility */}
      {data.soil?.fertility && (
        <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e0e8d8", padding:"12px 14px" }}>
          <div style={{ fontSize:9, fontWeight:700, color:"#999", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Soil Fertility</div>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
            {Object.entries(data.soil.fertility)
              .filter(([k]) => k !== "ph" && k !== "_sources"
                && !(["phosphorus","potassium"].includes(k) && (data.soil.fertility[k] === null || data.soil.fertility[k] === undefined)))
              .map(([k, v]) => {
                const noData = v === null || v === undefined;
                const c = noData ? "#bbb" : v === "High" ? "#15803d" : v === "Medium" ? "#854d0e" : v === "Low" ? "#991b1b" : "#bbb";
                const src = data.soil.fertility._sources?.[k];
                return (
                  <div key={k} title={src ? `Source: ${src}` : ""} style={{ flex:"1 1 80px", textAlign:"center", padding:"8px 6px", borderRadius:8,
                    background: noData ? "#fafafa" : "#f9fdf6", border:"1px solid #e0e8d8" }}>
                    <div style={{ fontSize:10, color:"#888", marginBottom:3, textTransform:"capitalize" }}>{k.replace(/_/g," ")}</div>
                    <div style={{ fontSize:12, fontWeight:700, color: c }}>{noData ? "—" : v}</div>
                  </div>
                );
              })}
          </div>
          <div style={{ marginTop:8, fontSize:10, color:"#aaa" }}>
            N &amp; OC: iSDA (Africa 30m) · POLARIS (USA 30m) · SoilGrids (global 250m) · P &amp; K require lab test
          </div>
          {data.soil?.slope_class && (
            <div style={{ marginTop:8, fontSize:11, color:"#555" }}>
              <strong>Terrain:</strong> {data.soil.slope_class}
            </div>
          )}
        </div>
      )}

      {/* Climate */}
      {data.climate?.name && (
        <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e0e8d8", padding:"12px 14px" }}>
          <div style={{ fontSize:9, fontWeight:700, color:"#999", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>Climate Zone</div>
          <div style={{ fontSize:13, fontWeight:700, color:"#1a2e1a" }}>{data.climate.name}</div>
          {data.climate.description && <div style={{ fontSize:11, color:"#555", marginTop:3 }}>{data.climate.description}</div>}
        </div>
      )}

      {/* Climate Resilience */}
      {data.resilience && (
        <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e0e8d8", padding:"14px 16px" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
            <div style={{ fontSize:9, fontWeight:700, color:"#999", textTransform:"uppercase", letterSpacing:1 }}>Climate Resilience</div>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ fontSize:20, fontWeight:800, color:
                data.resilience.overall_score >= 75 ? "#15803d" :
                data.resilience.overall_score >= 55 ? "#854d0e" :
                data.resilience.overall_score >= 35 ? "#b45309" : "#991b1b" }}>
                {data.resilience.overall_score}
              </div>
              <div style={{ fontSize:11, fontWeight:700, color:
                data.resilience.overall_score >= 75 ? "#15803d" :
                data.resilience.overall_score >= 55 ? "#854d0e" : "#991b1b" }}>
                {data.resilience.overall_label}
              </div>
            </div>
          </div>
          {Object.entries(data.resilience.risks).map(([key, r]) => {
            const labelMap = { climate_shift:"Climate Zone Shift", drought:"Drought", frost:"Frost / Cold", heat_stress:"Heat Stress", soil_degradation:"Soil Degradation" };
            const sc = r.score;
            const bc = sc >= 70 ? "#15803d" : sc >= 45 ? "#b45309" : "#991b1b";
            return (
              <div key={key} style={{ marginBottom:10 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:3 }}>
                  <span style={{ fontSize:12, fontWeight:600, color:"#1a2e1a" }}>{labelMap[key] || key}</span>
                  <span style={{ fontSize:11, fontWeight:700, color: bc }}>{r.label}</span>
                </div>
                <div style={{ height:5, borderRadius:3, background:"#e8f0e0", overflow:"hidden", marginBottom:3 }}>
                  <div style={{ height:"100%", width:`${sc}%`, borderRadius:3, background: bc }} />
                </div>
                <div style={{ fontSize:10, color:"#7a9a6a" }}>{r.detail}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Recommendations */}
      {data.recommendations?.length > 0 && (
        <div style={{ background:"#f0f7ec", borderRadius:12, border:"1px solid #c8e0b8", padding:"14px 16px" }}>
          <div style={{ fontSize:9, fontWeight:700, color:"#2d5a1b", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>
            Recommendations
          </div>
          {data.recommendations.map((r, i) => (
            <div key={i} style={{ display:"flex", gap:8, marginBottom: i < data.recommendations.length - 1 ? 8 : 0 }}>
              <span style={{ color:"#2d5a1b", fontWeight:700, flexShrink:0 }}>→</span>
              <span style={{ fontSize:12, color:"#1a2e1a", lineHeight:1.5 }}>{r}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Multi-crop picker ─────────────────────────────────────────────────────────
const MIX_COLORS = ["#2d5a1b","#b07d14","#1a5f7a","#7a1a5f"];

function MultiCropPicker({ cropMix, onChange, lang, CROPS, tCrop }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [openIdx, setOpenIdx] = useState(null);

  function updatePct(idx, rawVal) {
    const val = Math.max(1, Math.min(99, Number(rawVal) || 1));
    const delta = val - cropMix[idx].pct;
    if (delta === 0 || cropMix.length === 1) {
      onChange(cropMix.map((c, i) => i === idx ? { ...c, pct: val } : c));
      return;
    }
    // Distribute the negative delta proportionally among other crops
    const others = cropMix.filter((_, i) => i !== idx);
    const othersTotal = others.reduce((s, c) => s + c.pct, 0);
    let remaining = 100 - val;
    const next = cropMix.map((c, i) => {
      if (i === idx) return { ...c, pct: val };
      const share = othersTotal > 0 ? c.pct / othersTotal : 1 / others.length;
      return { ...c, pct: Math.max(1, Math.round(share * remaining)) };
    });
    // Fix rounding to ensure exact sum = 100
    const sum = next.reduce((s, c) => s + c.pct, 0);
    if (sum !== 100) {
      const fixIdx = next.findIndex((_, i) => i !== idx);
      next[fixIdx] = { ...next[fixIdx], pct: Math.max(1, next[fixIdx].pct + (100 - sum)) };
    }
    onChange(next);
  }

  function removeCrop(idx) {
    const next = cropMix.filter((_, i) => i !== idx);
    // redistribute pct equally
    if (next.length > 0) {
      const each = Math.round(100 / next.length);
      onChange(next.map((c, i) => ({ ...c, pct: i === next.length-1 ? 100 - each*(next.length-1) : each })));
    } else {
      onChange(next);
    }
  }

  function addCrop(cropName) {
    if (cropMix.length >= 4) return;
    if (cropMix.some(c => c.crop === cropName.toLowerCase())) return;
    const n = cropMix.length + 1;
    const each = Math.round(100 / n);
    const next = [...cropMix.map(c => ({ ...c, pct: each })), { crop: cropName.toLowerCase(), pct: 100 - each*(n-1) }];
    onChange(next);
    setOpenIdx(null);
    setSearch("");
  }

  const totalPct = cropMix.reduce((s, c) => s + (Number(c.pct) || 0), 0);
  const pctOk = Math.abs(totalPct - 100) < 1;

  return (
    <div style={{ border:"1px solid #c8d8c0", borderRadius:8, padding:"10px 12px", background:"#f8faf6", marginBottom:2 }}>
      <div style={{ fontSize:11, fontWeight:700, color:"#5a7a4a", marginBottom:8, textTransform:"uppercase", letterSpacing:0.5 }}>
        {t("intercropMix")}
      </div>

      {cropMix.map((cs, idx) => (
        <div key={idx} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
          {/* Color dot */}
          <div style={{ width:8, height:8, borderRadius:"50%", background:MIX_COLORS[idx], flexShrink:0 }} />
          {/* Crop name */}
          <div style={{ flex:1, fontSize:13, fontWeight:600, color:"#1a2e1a", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
            {tCrop(CROPS.find(c => c.toLowerCase() === cs.crop) || cs.crop, lang)}
          </div>
          {/* Pct input */}
          <div style={{ display:"flex", alignItems:"center", gap:4 }}>
            <input type="number" min={1} max={100} value={cs.pct}
              onChange={e => updatePct(idx, e.target.value)}
              style={{ width:52, padding:"4px 6px", fontSize:13, border:"1px solid #c8d8c0", borderRadius:5, textAlign:"right" }} />
            <span style={{ fontSize:12, color:"#888" }}>%</span>
          </div>
          {/* Remove */}
          <button onClick={() => removeCrop(idx)} style={{ background:"none", border:"none", color:"#c0392b", fontSize:16, cursor:"pointer", padding:"0 2px", lineHeight:1 }}>×</button>
        </div>
      ))}

      {/* Pct validation */}
      <div style={{ fontSize:11, color: pctOk ? "#7a9a6a" : "#c0392b", marginBottom:8, fontWeight:600 }}>
        {t("totalPct")} {totalPct}% {pctOk ? "✓" : t("mustEqual100")}
      </div>

      {/* Add crop button (up to 4) */}
      {cropMix.length < 4 && (
        <div style={{ position:"relative" }}>
          <button onClick={() => setOpenIdx(o => o === 0 ? null : 0)}
            style={{ width:"100%", padding:"7px 10px", fontSize:12, fontWeight:600, background:"#fff", color:"#2d5a1b", border:"1px dashed #2d5a1b", borderRadius:6, cursor:"pointer", textAlign:"left" }}>
            {t("addCropToMix")}
          </button>
          {openIdx === 0 && (
            <div style={{ position:"absolute", zIndex:300, top:"100%", left:0, right:0, background:"#fff", border:"1px solid #ddd", borderRadius:8, boxShadow:"0 4px 16px rgba(0,0,0,0.12)", maxHeight:220, display:"flex", flexDirection:"column" }}>
              <div style={{ padding:"6px 8px", borderBottom:"1px solid #eee" }}>
                <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
                  placeholder={t("searchCropsPlaceholder")}
                  style={{ width:"100%", padding:"5px 8px", fontSize:12, border:"1px solid #ccc", borderRadius:5, outline:"none", boxSizing:"border-box" }}
                  onClick={e => e.stopPropagation()} />
              </div>
              <div style={{ overflowY:"auto" }}>
                {CROPS.filter(c => {
                  const q = search.toLowerCase();
                  return (c.toLowerCase().includes(q) || tCrop(c, lang).toLowerCase().includes(q))
                    && !cropMix.some(m => m.crop === c.toLowerCase());
                }).map(c => (
                  <div key={c} onClick={() => addCrop(c)}
                    style={{ padding:"7px 12px", fontSize:13, cursor:"pointer" }}
                    onMouseEnter={e => e.currentTarget.style.background="#f0f6ea"}
                    onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                    {tCrop(c, lang)}
                  </div>
                ))}
              </div>
            </div>
          )}
          {openIdx === 0 && <div style={{ position:"fixed", inset:0, zIndex:299 }} onClick={() => { setOpenIdx(null); setSearch(""); }} />}
        </div>
      )}
    </div>
  );
}

// ── Drone Raster Upload Panel ─────────────────────────────────────────────────
const DRONE_ROLES = [
  { key:"blue",     label:"Blue",      nm:"~450–495 nm", required:false },
  { key:"green",    label:"Green",     nm:"~500–570 nm", required:false },
  { key:"red",      label:"Red",       nm:"~620–690 nm", required:true  },
  { key:"rededge",  label:"Red-Edge",  nm:"~700–730 nm", required:false },
  { key:"nir",      label:"NIR",       nm:"~750–900 nm", required:true  },
  { key:"swir1",    label:"SWIR",      nm:"~1550–1750 nm",required:false },
];

const DRONE_INDEX_NEEDS = {
  NDVI:"NIR + Red", GNDVI:"NIR + Green", EVI:"NIR + Red + Blue",
  MSAVI:"NIR + Red", NDRE:"NIR + Red-Edge", ReCI:"NIR + Red-Edge",
  GCI:"NIR + Green", NDWI:"Green + NIR", NDMI:"NIR + SWIR", PSRI:"Red + Green + Red-Edge",
};

function DroneUploadPanel({ API_BASE_URL }) {
  const { t } = useTranslation();
  const [mode,       setMode]       = useState("multiband"); // "multiband" | "individual"
  const [mbFile,     setMbFile]     = useState(null);
  const [bandMap,    setBandMap]    = useState({}); // role → band index (1-based)
  const [bandFiles,  setBandFiles]  = useState({}); // role → File
  const [numBands,   setNumBands]   = useState(""); // how many bands in the multiband file
  const [loading,    setLoading]    = useState(false);
  const [result,     setResult]     = useState(null);
  const [error,      setError]      = useState(null);

  async function run() {
    setLoading(true); setError(null); setResult(null);
    try {
      const fd = new FormData();
      fd.append("mode", mode);
      if (mode === "multiband") {
        if (!mbFile) { setError("Please upload a multiband GeoTIFF."); setLoading(false); return; }
        fd.append("multiband", mbFile);
        fd.append("band_map", JSON.stringify(
          Object.fromEntries(Object.entries(bandMap).filter(([,v]) => v).map(([k,v]) => [k, Number(v)]))
        ));
      } else {
        const assigned = Object.entries(bandFiles).filter(([,f]) => f);
        if (assigned.length < 2) { setError("Upload at least NIR and Red band files."); setLoading(false); return; }
        for (const [role, file] of assigned) fd.append(role, file);
      }
      const res = await fetch(`${API_BASE_URL}/drone-analysis`, { method:"POST", body:fd });
      const data = await res.json();
      if (data.error) setError(data.error);
      else setResult(data);
    } catch (e) {
      setError("Upload failed: " + e.message);
    }
    setLoading(false);
  }

  const indexColors = { null: "#999", good: "#2d5a1b", warn: "#c07000" };

  return (
    <div style={{ padding:"16px 0" }}>
      <div style={{ fontSize:13, fontWeight:700, color:"#1a2e1a", marginBottom:4 }}>{t("droneRasterAnalysis")}</div>
      <div style={{ fontSize:11, color:"#888", marginBottom:14, lineHeight:1.5 }}>
        {t("droneUploadDesc")}
      </div>

      {/* Mode toggle */}
      <div style={{ display:"flex", gap:6, marginBottom:14 }}>
        {[["multiband",t("singleMultibandGeotiff")],["individual",t("individualBandFiles")]].map(([k,l]) => (
          <button key={k} onClick={() => { setMode(k); setResult(null); setError(null); }} style={{
            flex:1, padding:"8px 6px", fontSize:12, fontWeight:600, borderRadius:7, cursor:"pointer",
            background: mode===k ? "#2d5a1b" : "#f0f6ea",
            color: mode===k ? "#fff" : "#2d5a1b",
            border:`1px solid ${mode===k ? "#2d5a1b" : "#c8d8c0"}`,
          }}>{l}</button>
        ))}
      </div>

      {/* Multiband mode */}
      {mode === "multiband" && (
        <div>
          <label style={{ fontSize:12, fontWeight:600, color:"#444", display:"block", marginBottom:6 }}>
            {t("uploadMultibandLabel")}
          </label>
          <input type="file" accept=".tif,.tiff" onChange={e => { setMbFile(e.target.files?.[0] || null); setResult(null); }}
            style={{ fontSize:12, marginBottom:10, width:"100%", boxSizing:"border-box" }} />

          <div style={{ fontSize:12, fontWeight:600, color:"#444", marginBottom:6 }}>
            {t("howManyBands")}
          </div>
          <input type="number" min={1} max={50} placeholder="e.g. 5"
            value={numBands} onChange={e => setNumBands(e.target.value)}
            style={{ width:"100%", padding:"7px 10px", fontSize:13, border:"1px solid #c8d8c0", borderRadius:6, marginBottom:12, boxSizing:"border-box" }} />

          <div style={{ fontSize:12, fontWeight:700, color:"#444", marginBottom:8 }}>
            {t("assignBandNumbers")}
            <span style={{ fontWeight:400, color:"#999", marginLeft:6 }}>{t("bandIndexNote")}</span>
          </div>
          {DRONE_ROLES.map(r => (
            <div key={r.key} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
              <div style={{ width:80, fontSize:12, fontWeight:600, color:"#333", flexShrink:0 }}>{r.label}</div>
              <div style={{ fontSize:10, color:"#aaa", flex:1 }}>{r.nm}</div>
              <input type="number" min={1} max={numBands || 50} placeholder={r.required ? "required" : "optional"}
                value={bandMap[r.key] || ""}
                onChange={e => setBandMap(prev => ({ ...prev, [r.key]: e.target.value }))}
                style={{ width:80, padding:"5px 8px", fontSize:12, border:`1px solid ${r.required?"#c8d8c0":"#ddd"}`, borderRadius:5, background: r.required?"#fff":"#fafafa" }} />
            </div>
          ))}
        </div>
      )}

      {/* Individual bands mode */}
      {mode === "individual" && (
        <div>
          <div style={{ fontSize:12, fontWeight:700, color:"#444", marginBottom:8 }}>
            {t("uploadOneGeotiffPerBand")}
            <span style={{ fontWeight:400, color:"#999", marginLeft:6 }}>{t("nirRedRequired")}</span>
          </div>
          {DRONE_ROLES.map(r => (
            <div key={r.key} style={{ marginBottom:10 }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:3 }}>
                <span style={{ fontSize:12, fontWeight:600, color:"#333" }}>{r.label}</span>
                <span style={{ fontSize:10, color:"#aaa" }}>{r.nm}</span>
                {r.required && <span style={{ fontSize:10, color:"#c0392b", fontWeight:700 }}>required</span>}
              </div>
              <input type="file" accept=".tif,.tiff"
                onChange={e => setBandFiles(prev => ({ ...prev, [r.key]: e.target.files?.[0] || undefined }))}
                style={{ fontSize:11, width:"100%", boxSizing:"border-box" }} />
            </div>
          ))}
        </div>
      )}

      {/* Which indices will be computed — live preview */}
      <div style={{ background:"#f0f6ea", borderRadius:7, padding:"10px 12px", marginTop:10, marginBottom:12 }}>
        <div style={{ fontSize:11, fontWeight:700, color:"#5a7a4a", marginBottom:6, textTransform:"uppercase", letterSpacing:0.5 }}>{t("indicesComputed")}</div>
        <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
          {Object.entries(DRONE_INDEX_NEEDS).map(([name, needs]) => {
            const rolesNeeded = needs.toLowerCase().replace(/-/g,"").split("+").map(s=>s.trim().replace("red edge","rededge").replace("swir","swir1").replace(" ",""));
            let available;
            if (mode === "multiband") available = rolesNeeded.every(r => bandMap[r]);
            else available = rolesNeeded.every(r => bandFiles[r]);
            return (
              <span key={name} style={{
                fontSize:11, fontWeight:600, padding:"3px 8px", borderRadius:12,
                background: available ? "#d4edbc" : "#f0f0f0",
                color: available ? "#2d5a1b" : "#bbb",
              }} title={needs}>
                {name}
              </span>
            );
          })}
        </div>
        <div style={{ fontSize:10, color:"#aaa", marginTop:6 }}>{t("missingBandsNote")}</div>
      </div>

      {error && <div style={{ background:"#fef2f2", border:"1px solid #fecaca", color:"#991b1b", borderRadius:7, padding:"10px 12px", fontSize:12, marginBottom:10 }}>{error}</div>}

      <button onClick={run} disabled={loading} style={{
        width:"100%", padding:"11px", fontSize:13, fontWeight:700,
        background: loading ? "#7aab5a" : "#2d5a1b", color:"#fff",
        border:"none", borderRadius:8, cursor: loading ? "default" : "pointer",
      }}>
        {loading ? t("processingBtn") : t("runDroneAnalysis")}
      </button>

      {/* Results */}
      {result && (
        <div style={{ marginTop:16 }}>
          <div style={{ fontSize:13, fontWeight:700, color:"#1a2e1a", marginBottom:10 }}>{t("droneResults")} ({result.bands_provided?.join(", ")})</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
            {Object.entries(result.indices || {}).map(([name, stats]) => (
              <div key={name} style={{ background:"#f8faf6", border:"1px solid #e2ead8", borderRadius:8, padding:"10px 12px" }}>
                <div style={{ fontSize:12, fontWeight:700, color:"#1a2e1a" }}>{name}</div>
                <div style={{ fontSize:18, fontWeight:700, color:"#2d5a1b", marginTop:2 }}>{stats.mean?.toFixed(3) ?? "—"}</div>
                <div style={{ fontSize:10, color:"#aaa", marginTop:2 }}>p10: {stats.p10?.toFixed(3)} · p90: {stats.p90?.toFixed(3)}</div>
              </div>
            ))}
          </div>
          {result.skipped_indices && Object.keys(result.skipped_indices).length > 0 && (
            <div style={{ marginTop:10, fontSize:11, color:"#999" }}>
              {t("skippedIndices")}: {Object.entries(result.skipped_indices).map(([k,v]) => `${k} (${v})`).join(", ")}
            </div>
          )}
          {/* Band reflectance stats */}
          <div style={{ marginTop:14 }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#444", marginBottom:6 }}>{t("bandReflectance")}</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
              {Object.entries(result.band_stats || {}).map(([role, stats]) => (
                <div key={role} style={{ background:"#fff", border:"1px solid #ddd", borderRadius:6, padding:"6px 10px" }}>
                  <div style={{ fontSize:10, color:"#888", textTransform:"uppercase" }}>{role}</div>
                  <div style={{ fontSize:13, fontWeight:600 }}>{stats.mean?.toFixed(4) ?? "—"}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  const embedMode = new URLSearchParams(window.location.search).has("embed");
  const [coordinates,     setCoordinates]     = useState(null);
  const [crop,            setCrop]            = useState("maize");
  // Multi-crop: [{crop, pct}] — when length > 1 overrides single crop
  const [cropMix,         setCropMix]         = useState([]);
  const [multiCropMode,   setMultiCropMode]   = useState(false);
  const [analysisDate,    setAnalysisDate]    = useState(() => new Date().toISOString().slice(0, 10));
  const [activeTab,       setActiveTab]       = useState("field"); // "field" | "irrigation"
  const [showDashboard,   setShowDashboard]   = useState(() => {
    // Auto-open dashboard if URL has ?token= or ?invite=, or if a session JWT exists
    const p = new URLSearchParams(window.location.search);
    if (p.has("token") || p.has("invite")) return true;
    return !!localStorage.getItem("of_admin_token");
  });
  const [result,          setResult]          = useState(null);
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState(null);
  const [serverBusy,      setServerBusy]      = useState(false);
  const [queueDepth,      setQueueDepth]      = useState(0);
  const [mapLayer,        setMapLayer]        = useState("labels");
  const [climateExpanded, setClimateExpanded] = useState(false);
  const [showNdvi,        setShowNdvi]        = useState(true);
  const [ndviTileUrl,     setNdviTileUrl]     = useState(null);
  const [drawnPolygon,    setDrawnPolygon]    = useState(null);
  const [lang,            setLang]            = useState("en");
  const [searchVal,       setSearchVal]       = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const searchDebounce = useRef(null);
  const [mapCenter,       setMapCenter]       = useState(() => {
    // Try to use a cached location from a previous visit so the map opens there instantly
    try {
      const c = JSON.parse(sessionStorage.getItem("of_last_loc") || "null");
      if (c) return c;
    } catch {}
    return [-1.102, 36.638]; // fallback: Limuru
  });
  const [mapZoom,         setMapZoom]         = useState(14);
  const [isMobile,        setIsMobile]        = useState(false);
  const [sheetSnap,       setSheetSnap]       = useState("bottom"); // bottom | half | full
  const [langOpen,        setLangOpen]        = useState(false);
  const [areaUnit,         setAreaUnit]         = useState("ha");
  const locateTriggerRef = useRef(null);
  const mapFlyRef = useRef(null);
  const [panelWidth, setPanelWidth] = useState(360);
  const dragRef = useRef(null); // { startX, startW }
  const [scoutOpen,      setScoutOpen]      = useState(false);
  const [scoutAnswers,   setScoutAnswers]   = useState({}); // { [riskName]: "yes"|"no" }
  const [showDiseaseModal, setShowDiseaseModal] = useState(false);
  const [diseaseImg,       setDiseaseImg]       = useState(null);
  const [diseaseResult,    setDiseaseResult]    = useState(null);
  const [diseaseLoading,   setDiseaseLoading]   = useState(false);
  // Save analysis (embed mode)
  const [embedUser,        setEmbedUser]        = useState(null);
  const [saveModalOpen,    setSaveModalOpen]    = useState(false);
  const [saveName,         setSaveName]         = useState("");
  const [saveOrgId,        setSaveOrgId]        = useState(null);
  const [savingAnalysis,   setSavingAnalysis]   = useState(false);
  const [saveMsg,          setSaveMsg]          = useState(null);
  const [savedAnalyses,    setSavedAnalyses]    = useState([]);
  const [loadPanelOpen,    setLoadPanelOpen]    = useState(false);

  // Fetch logged-in user's orgs so we know where to save analyses
  useEffect(() => {
    const jwt = localStorage.getItem("of_admin_token");
    if (!jwt) return;
    const API_BASE = window.location.hostname === "localhost" ? "http://localhost:8000" : "";
    fetch(`${API_BASE}/auth/me`, { headers: { Authorization: `Bearer ${jwt}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d && d.email) {
          setEmbedUser(d);
          setSaveOrgId(d.orgs?.[0]?.id || null);
          const orgId = d.orgs?.[0]?.id;
          if (orgId) {
            fetch(`${API_BASE}/analyses/org/${orgId}`, { headers: { Authorization: `Bearer ${jwt}` } })
              .then(r => r.ok ? r.json() : null)
              .then(data => { if (Array.isArray(data) && data.length) setSavedAnalyses(data); })
              .catch(() => {});
          }
        }
      }).catch(() => {});
  }, []);

  // Separately fetch saved analyses for Load panel — independent of embedUser
  useEffect(() => {
    const jwt = localStorage.getItem("of_admin_token");
    if (!jwt) return;
    const API_BASE = window.location.hostname === "localhost" ? "http://localhost:8000" : "";
    fetch(`${API_BASE}/analyses/mine`, { headers: { Authorization: `Bearer ${jwt}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (Array.isArray(data) && data.length) setSavedAnalyses(data); })
      .catch(() => {});
  }, []);
  const [diseaseError,     setDiseaseError]     = useState(null);
  const [yriPriceKg,      setYriPriceKg]      = useState(0.5);
  const [cropSearch,      setCropSearch]      = useState("");
  const [cropDropOpen,    setCropDropOpen]    = useState(false);
  const cropDropRef = useRef(null);
  const [areaMode,         setAreaMode]         = useState(false);
  const [areaResult,       setAreaResult]       = useState(null);
  const [rasterPanelOpen,  setRasterPanelOpen]  = useState(false);
  const [activeRasters,    setActiveRasters]    = useState({});
  const diseaseInputRef = useRef(null);
  const dragStartY = useRef(null);
  const { t, i18n: i18nHook } = useTranslation();
  // Keep lang state in sync with i18n (covers programmatic changes from other components)
  useEffect(() => {
    const onLangChange = (lng) => setLang(lng);
    i18nHook.on("languageChanged", onLangChange);
    return () => i18nHook.off("languageChanged", onLangChange);
  }, [i18nHook]);

  // Shoelace formula on WGS84 coords → approximate area in hectares
  function computeAreaHa(coords) {
    if (!coords || coords.length < 3) return null;
    const R = 6371000; // Earth radius in metres
    const toRad = d => d * Math.PI / 180;
    let area = 0;
    const n = coords.length;
    for (let i = 0; i < n; i++) {
      const [lon1, lat1] = coords[i];
      const [lon2, lat2] = coords[(i + 1) % n];
      area += toRad(lon2 - lon1) * (2 + Math.sin(toRad(lat1)) + Math.sin(toRad(lat2)));
    }
    const areaSqM = Math.abs(area * R * R / 2);
    return areaSqM / 10000; // hectares
  }

  function formatArea(ha, unit) {
    if (ha == null) return null;
    if (unit === "ha")   return `${ha.toFixed(2)} ha`;
    if (unit === "ac")   return `${(ha * 2.47105).toFixed(2)} ac`;
    if (unit === "sqkm") return `${(ha / 100).toFixed(4)} km²`;
    if (unit === "sqm")  return `${Math.round(ha * 10000).toLocaleString()} m²`;
    return `${ha.toFixed(2)} ha`;
  }

  const handlePrint = () => printFieldReport(result);
  const activeLayer = MAP_LAYERS[mapLayer];
  const API_BASE_URL = window.location.hostname === "localhost"
    ? "http://localhost:8000"
    : "https://nicolasljulia-openfarm.hf.space";

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Poll queue status while a request is in flight so we can show a queue banner
  useEffect(() => {
    if (!loading) { setServerBusy(false); setQueueDepth(0); return; }
    let alive = true;
    const poll = async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/queue-status`);
        if (!alive) return;
        if (r.ok) {
          const d = await r.json();
          setServerBusy(d.busy);
          setQueueDepth(d.queued || 0);
        }
      } catch (_) {}
    };
    poll();
    const iv = setInterval(poll, 4000);
    return () => { alive = false; clearInterval(iv); };
  }, [loading, API_BASE_URL]);

  function handleLocateMe() {
    if (locateTriggerRef.current) locateTriggerRef.current();
  }

  // Auto-expand sheet when results arrive
  useEffect(() => {
    if (isMobile && result) setSheetSnap("half");
  }, [result, isMobile]);

  const isPro = !!(embedUser?.orgs?.length > 0);
  const MAX_AREA_AC = isPro ? 100 : 20;
  const MAX_AREA_HA = MAX_AREA_AC * 0.404686;

  function checkAreaLimit(coords) {
    const ha = computeAreaHa(coords);
    if (ha && ha > MAX_AREA_HA) {
      setError(`Field is too large (${(ha * 2.47105).toFixed(1)} ac). Maximum allowed is ${MAX_AREA_AC} acres${isPro ? "" : " (upgrade to Pro for up to 100 ac)"}. Please draw a smaller area.`);
      return false;
    }
    return true;
  }

  async function handleShapefileImport(file) {
    try {
      setError(null);
      const buf = await file.arrayBuffer();
      let geojson;
      if (file.name.endsWith(".zip")) {
        geojson = await shp(buf);
      } else {
        geojson = JSON.parse(new TextDecoder().decode(buf));
      }
      const features = geojson.features ?? (geojson.type === "Feature" ? [geojson] : []);
      const poly = features.find(f => f.geometry?.type === "Polygon" || f.geometry?.type === "MultiPolygon");
      if (!poly) { setError("No polygon found in the shapefile."); return; }
      let coords = poly.geometry.type === "MultiPolygon"
        ? poly.geometry.coordinates[0][0]
        : poly.geometry.coordinates[0];
      // GeoJSON is [lon,lat], same as our internal format
      if (!checkAreaLimit(coords)) return;
      setCoordinates(coords);
      setDrawnPolygon(coords);
      setResult(null); setNdviTileUrl(null); setError(null); setAreaResult(null);
      // Zoom map to imported polygon
      const lats = coords.map(c => c[1]), lons = coords.map(c => c[0]);
      const bounds = [[Math.min(...lats), Math.min(...lons)], [Math.max(...lats), Math.max(...lons)]];
      setTimeout(() => { if (mapFlyRef.fitBounds) mapFlyRef.fitBounds(bounds); }, 100);
    } catch (e) {
      setError("Failed to read shapefile: " + (e.message || "unknown error"));
    }
  }

  function loadSavedAnalysis(analysis) {
    const raw = analysis.polygon;
    const coords = Array.isArray(raw) ? raw
      : raw?.coordinates?.[0] || raw?.geometry?.coordinates?.[0];
    if (!coords || coords.length < 3) return;
    setCoordinates(coords);
    setDrawnPolygon(coords);
    setResult(analysis.result || null);
    setNdviTileUrl(null); setError(null); setAreaResult(null);
    const savedCrop = analysis.result?.crop || analysis.result?.inputs?.crop;
    if (savedCrop) setCrop(savedCrop.toLowerCase());
    // Fly map to field centroid
    const lat = coords.reduce((s, p) => s + p[1], 0) / coords.length;
    const lon = coords.reduce((s, p) => s + p[0], 0) / coords.length;
    if (mapFlyRef.current) mapFlyRef.current(lat, lon, 14);
    setLoadPanelOpen(false);
    if (isMobile) setSheetSnap("half");
  }

  function onFieldDrawn(e) {
    const lls = e.layer.getLatLngs()[0];
    const coords = lls.map(p => [p.lng, p.lat]);
    if (!checkAreaLimit(coords)) { e.layer.remove(); return; }
    setCoordinates(coords);
    setResult(null); setNdviTileUrl(null); setError(null);
    if (isMobile) setSheetSnap("bottom");
  }
  function onTouchFieldComplete(coords) {
    if (!checkAreaLimit(coords)) return;
    setCoordinates(coords);
    setDrawnPolygon(coords);
    setResult(null); setNdviTileUrl(null); setError(null);
  }

  async function analyzeField() {
    if (!coordinates) return;
    setLoading(true); setError(null); setResult(null); setAreaResult(null); setNdviTileUrl(null);
    if (isMobile) setSheetSnap("peek");
    try {
      const today = new Date().toISOString().slice(0, 10);
      if (areaMode) {
        const res = await axios.post(`${API_BASE_URL}/analyze-area`, {
          coordinates,
          analysis_date: analysisDate !== today ? analysisDate : "",
        });
        if (res.data.error) setError(res.data.error);
        else setAreaResult(res.data);
      } else {
        const payload = {
          coordinates,
          analysis_date: analysisDate !== today ? analysisDate : "",
        };
        if (multiCropMode && cropMix.length > 1) {
          payload.crops = cropMix;
        } else {
          payload.crop_type = crop.toLowerCase();
        }
        const res = await axios.post(`${API_BASE_URL}/analyze`, payload);
        if (res.data.error) { setError(res.data.error); }
        else {
          setResult(res.data);
          if (res.data.ndvi_tile_url && showNdvi) setNdviTileUrl(res.data.ndvi_tile_url);
        }
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        setError("Too many requests — you've sent too many analyses in the last minute. Please wait a moment and try again.");
      } else if (status === 503) {
        setError("Server is currently processing many requests. Your analysis was queued but timed out — please try again in a few seconds.");
      } else {
        setError("Analysis failed — is the backend running?");
      }
    }
    setLoading(false);
  }

  async function saveAnalysis() {
    if (!saveName.trim() || !result) { setSaveMsg("Missing name or result"); return; }
    const orgId = saveOrgId || embedUser?.orgs?.[0]?.id;
    if (!orgId) { setSaveMsg("No org selected — contact your admin"); return; }
    setSavingAnalysis(true); setSaveMsg(null);
    const jwt = localStorage.getItem("of_admin_token");
    const API_BASE = window.location.hostname === "localhost" ? "http://localhost:8000" : "";
    try {
      await fetch(`${API_BASE}/analyses/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` },
        body: JSON.stringify({
          org_id: orgId, name: saveName.trim(),
          polygon: drawnPolygon || coordinates || [], crop, analysis_date: analysisDate, result,
        }),
      });
      setSaveMsg("✓ Saved");
      setSaveModalOpen(false); setSaveName("");
    } catch(e) { setSaveMsg("Error: " + (e.message || "failed")); }
    setSavingAnalysis(false);
  }

  async function analyzeDisease() {
    if (!diseaseImg) return;
    setDiseaseLoading(true); setDiseaseError(null); setDiseaseResult(null);
    try {
      const formData = new FormData();
      formData.append("file", diseaseImg);
      formData.append("crop_type", crop);
      const res = await axios.post(`${API_BASE_URL}/api/analyze`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDiseaseResult(res.data);
    } catch { setDiseaseError("Detection failed — check that the backend is running."); }
    setDiseaseLoading(false);
  }

  function flyToResult(lat, lon) {
    setMapLayer("labels"); // switch to Sat+Labels so farm boundaries are visible
    setSearchVal("");
    setSearchSuggestions([]);
    if (mapFlyRef.current) mapFlyRef.current(parseFloat(lat), parseFloat(lon));
  }

  async function handleSearch(val) {
    if (!val.trim()) return;
    const parts = val.split(",").map(s => parseFloat(s.trim()));
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1]) && parts[0] >= -90 && parts[0] <= 90) {
      flyToResult(parts[0], parts[1]); return;
    }
    try {
      const res = await axios.get(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(val)}&limit=1`,
        { headers:{ "Accept-Language":"en" } }
      );
      if (res.data.length > 0) {
        flyToResult(res.data[0].lat, res.data[0].lon);
      } else alert("Place not found.");
    } catch { alert("Search failed."); }
  }

  function handleSearchInput(val) {
    setSearchVal(val);
    setSearchSuggestions([]);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    if (val.trim().length < 3) return;
    searchDebounce.current = setTimeout(async () => {
      try {
        const res = await axios.get(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(val)}&limit=5`,
          { headers:{ "Accept-Language":"en" } }
        );
        setSearchSuggestions(res.data);
      } catch { /* silently ignore */ }
    }, 350);
  }

  // Sheet snap heights
  const SNAPS = { bottom:"68px", peek:"160px", half:"55vh", full:"91vh" };
  const handleBarRef = useRef(null);
  const dragOnHandle = useRef(false);

  // Only start drag tracking when touch begins on the handle bar, not content
  function onHandleTouchStart(e) {
    dragOnHandle.current = true;
    dragStartY.current = e.touches[0].clientY;
    e.stopPropagation();
  }
  function onHandleTouchEnd(e) {
    if (!dragOnHandle.current || dragStartY.current == null) return;
    const dy = dragStartY.current - e.changedTouches[0].clientY;
    const order = ["bottom","peek","half","full"];
    const idx = order.indexOf(sheetSnap);
    if (dy > 40 && idx < 3) setSheetSnap(order[idx+1]);
    if (dy < -40 && idx > 0) setSheetSnap(order[idx-1]);
    dragStartY.current = null;
    dragOnHandle.current = false;
  }

  // ── PANEL CONTENT (shared desktop + mobile) ────────────────────────────
  const tabBar = (
    <div style={{ display:"flex", borderBottom:"1px solid #dde8d0", background:"#f5f9f3", flexShrink:0 }}>
      {[["field",t("fieldEvaluatorTab")],["irrigation",t("irrigationCalcTab")],...(embedMode?[["drone",t("droneDataTab")]]:[])]
        .map(([key,label]) => {
        return (
        <button key={key} onClick={() => setActiveTab(key)} style={{
          flex:1, padding:"10px 8px", fontSize:12, fontWeight:600,
          border:"none", borderBottom: activeTab===key ? "2px solid #2d5a1b" : "2px solid transparent",
          background:"transparent", color: activeTab===key ? "#2d5a1b" : "#7a9a6a",
          cursor:"pointer", transition:"all 0.15s",
        }}>{label}</button>
        );
      })}
    </div>
  );

  const panel = (
    <>
      {tabBar}
      {activeTab === "irrigation" && (
        <IrrigationCalc coordinates={coordinates} apiBase={API_BASE_URL} isPro={isPro} initialCrop={crop} />
      )}
      {embedMode && activeTab === "drone" && (
        <div style={{ overflowY:"auto", flex:1, padding:"0 16px" }}>
          <DroneUploadPanel API_BASE_URL={API_BASE_URL} />
        </div>
      )}
      {activeTab === "field" && <>

      {/* Load saved analysis — pro only */}
      {isPro && savedAnalyses.length > 0 && (
        <div style={{ background:"#fff", borderRadius:10, border:"0.5px solid #e0e0d8", flexShrink:0, overflow:"hidden" }}>
          <button onClick={() => setLoadPanelOpen(v => !v)}
            style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px", background:"none", border:"none", cursor:"pointer" }}>
            <span style={{ fontSize:11, fontWeight:700, color:"#888", textTransform:"uppercase", letterSpacing:"0.05em" }}>Load Saved Analysis</span>
            <span style={{ fontSize:11, color:"#aaa" }}>{loadPanelOpen ? "▲" : "▼"}</span>
          </button>
          {loadPanelOpen && (
            <div style={{ borderTop:"0.5px solid #f0f0e8", maxHeight:260, overflowY:"auto" }}>
              {savedAnalyses.map(a => {
                const cropName = a.result?.crop || a.result?.inputs?.crop || "Unknown crop";
                const areaHa   = a.result?.area_ha || a.result?.inputs?.area_ha;
                return (
                  <button key={a.id} onClick={() => loadSavedAnalysis(a)}
                    style={{ width:"100%", textAlign:"left", padding:"9px 14px", background:"none", border:"none",
                      borderBottom:"0.5px solid #f5f5f0", cursor:"pointer", display:"flex", flexDirection:"column", gap:2 }}
                    onMouseEnter={e => e.currentTarget.style.background="#f5f9f3"}
                    onMouseLeave={e => e.currentTarget.style.background="none"}>
                    <div style={{ fontSize:13, fontWeight:600, color:"#1a2e1a" }}>{a.name || `Analysis #${a.id}`}</div>
                    <div style={{ fontSize:11, color:"#7a9a6a" }}>
                      {cropName}{areaHa ? ` · ${Number(areaHa).toFixed(1)} ha` : ""}
                      {a.created_at ? ` · ${new Date(a.created_at).toLocaleDateString()}` : ""}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div style={s.card}>
        <div style={s.cardLabel}>{t("step1")}</div>
        <p style={s.hint}>{t("step1desc")}</p>
        {coordinates && (
          <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
            <div style={s.chip}>{t("fieldCaptured")} — {coordinates.length} {t("points")}</div>
            <button onClick={() => { setCoordinates(null); setDrawnPolygon(null); setResult(null); setNdviTileUrl(null); setError(null); setAreaResult(null); }}
              style={{ fontSize:11, fontWeight:600, color:"#991b1b", background:"#fff5f5", border:"1px solid #fca5a5", borderRadius:6, padding:"3px 9px", cursor:"pointer" }}>
              ✕ Clear field
            </button>
          </div>
        )}
        {isPro && (
          <label style={{ display:"flex", alignItems:"center", gap:6, marginTop:8, cursor:"pointer" }}>
            <input type="file" accept=".zip,.geojson,.json" style={{ display:"none" }}
              onChange={e => { const f = e.target.files?.[0]; if (f) handleShapefileImport(f); e.target.value = ""; }} />
            <span style={{ fontSize:12, fontWeight:600, color:"#2d5a1b", background:"#f0f7ec", border:"1px solid #c8e0b8", borderRadius:6, padding:"4px 10px" }}>
              ↑ Import Shapefile / GeoJSON
            </span>
          </label>
        )}
      </div>

      {/* Load Rasters panel — pro only */}
      {isPro && <div style={{ background:"#fff", borderRadius:10, border:"0.5px solid #e0e0d8", flexShrink:0, overflow:"hidden" }}>
        <button onClick={() => setRasterPanelOpen(v => !v)}
          style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px", background:"none", border:"none", cursor:"pointer" }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:11, fontWeight:700, color:"#888", textTransform:"uppercase", letterSpacing:"0.05em" }}>Data Layers</span>
            {Object.values(activeRasters).filter(Boolean).length > 0 && (
              <span style={{ fontSize:10, fontWeight:700, background:"#2d5a1b", color:"#fff", borderRadius:10, padding:"1px 7px" }}>
                {Object.values(activeRasters).filter(Boolean).length} on
              </span>
            )}
          </div>
          <span style={{ fontSize:11, color:"#aaa" }}>{rasterPanelOpen ? "▲" : "▼"}</span>
        </button>
        {rasterPanelOpen && (
          <div style={{ padding:"0 14px 12px", borderTop:"0.5px solid #f0f0e8" }}>
            {["Terrain","Soil","Vegetation","Climate"].map(cat => (
              <div key={cat} style={{ marginTop:10 }}>
                <div style={{ fontSize:10, fontWeight:700, color:"#2d5a1b", marginBottom:4, textTransform:"uppercase", letterSpacing:"0.04em" }}>{cat}</div>
                {RASTER_LAYERS.filter(r => r.category === cat).map(r => (
                  <label key={r.id} style={{ display:"flex", alignItems:"center", gap:8, fontSize:12, color:"#333", cursor:"pointer", marginBottom:5, userSelect:"none" }}>
                    <input type="checkbox" checked={!!activeRasters[r.id]}
                      onChange={e => setActiveRasters(prev => ({ ...prev, [r.id]: e.target.checked }))}
                      style={{ accentColor:"#2d5a1b", cursor:"pointer" }} />
                    {r.label}
                  </label>
                ))}
              </div>
            ))}
            {Object.values(activeRasters).some(Boolean) && (
              <button onClick={() => setActiveRasters({})}
                style={{ marginTop:8, fontSize:11, color:"#991b1b", background:"none", border:"none", cursor:"pointer", padding:0 }}>
                {t("clearAll")}
              </button>
            )}
          </div>
        )}
      </div>}

      <div style={s.card}>
        <div style={s.cardLabel}>{t("step2")}</div>
        {/* Area analysis toggle — pro only */}
        {isPro && (
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, cursor: "pointer", fontSize: 13, color: "#1a2e1a", userSelect: "none" }}>
          <input type="checkbox" checked={areaMode} onChange={e => { setAreaMode(e.target.checked); setResult(null); setAreaResult(null); }}
            style={{ width: 15, height: 15, accentColor: "#2d5a1b", cursor: "pointer" }} />
          <span style={{ fontWeight: 600 }}>{t("runAreaAnalysis")}</span>
          <span style={{ fontSize: 11, color: "#7a9a6a" }}>{t("findWhatGrowsBest")}</span>
        </label>
        )}
        {/* Multi-crop toggle */}
        <label style={{ display:"flex", alignItems:"center", gap:7, marginBottom:6, cursor:"pointer", fontSize:12, color:"#5a7a4a", userSelect:"none" }}>
          <input type="checkbox" checked={multiCropMode} onChange={e => {
            setMultiCropMode(e.target.checked);
            if (e.target.checked && cropMix.length === 0) setCropMix([{crop: crop, pct: 100}]);
          }} style={{ accentColor:"#2d5a1b" }} />
          {t("intercropping")}
        </label>

        {/* ── Single crop picker ── */}
        {!multiCropMode && (
          <div ref={cropDropRef} style={{ position:"relative", opacity: areaMode ? 0.4 : 1, pointerEvents: areaMode ? "none" : "auto" }}>
            <div
              onClick={() => { if (!areaMode) { setCropDropOpen(o => !o); setCropSearch(""); } }}
              style={{ ...s.cropSelect, display:"flex", justifyContent:"space-between", alignItems:"center", cursor:"pointer", userSelect:"none" }}>
              <span>{tCrop(CROPS.find(c => c.toLowerCase() === crop) || crop, lang)}</span>
              <span style={{ fontSize:10, color:"#999", marginLeft:6 }}>▾</span>
            </div>
            {cropDropOpen && !areaMode && (
              <div style={{ position:"absolute", zIndex:200, top:"100%", left:0, right:0, background:"#fff", border:"1px solid #ddd", borderRadius:8, boxShadow:"0 4px 16px rgba(0,0,0,0.12)", maxHeight:280, display:"flex", flexDirection:"column" }}>
                <div style={{ padding:"6px 8px", borderBottom:"1px solid #eee" }}>
                  <input autoFocus value={cropSearch} onChange={e => setCropSearch(e.target.value)}
                    placeholder="Search crops..." onClick={e => e.stopPropagation()}
                    style={{ width:"100%", padding:"5px 8px", fontSize:12, border:"1px solid #ccc", borderRadius:5, outline:"none", boxSizing:"border-box" }} />
                </div>
                <div style={{ overflowY:"auto", maxHeight:220 }}>
                  {CROPS.filter(c => { const q=cropSearch.toLowerCase(); return c.toLowerCase().includes(q)||tCrop(c,lang).toLowerCase().includes(q); }).map(c => (
                    <div key={c} onClick={() => { setCrop(c.toLowerCase()); setCropDropOpen(false); setCropSearch(""); }}
                      style={{ padding:"7px 12px", fontSize:13, cursor:"pointer", background:c.toLowerCase()===crop?"#f0f6ea":"transparent", color:c.toLowerCase()===crop?"#2d5a1b":"#1a2e1a", fontWeight:c.toLowerCase()===crop?600:400 }}
                      onMouseEnter={e=>{ if(c.toLowerCase()!==crop) e.currentTarget.style.background="#f8f8f8"; }}
                      onMouseLeave={e=>{ if(c.toLowerCase()!==crop) e.currentTarget.style.background="transparent"; }}>
                      {tCrop(c, lang)}
                    </div>
                  ))}
                  {CROPS.filter(c=>{ const q=cropSearch.toLowerCase(); return c.toLowerCase().includes(q)||tCrop(c,lang).toLowerCase().includes(q); }).length===0 && (
                    <div style={{ padding:"10px 12px", fontSize:12, color:"#999" }}>No crops found</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        {cropDropOpen && !multiCropMode && <div style={{ position:"fixed", inset:0, zIndex:199 }} onClick={() => { setCropDropOpen(false); setCropSearch(""); }} />}

        {/* ── Multi-crop picker ── */}
        {multiCropMode && !areaMode && (
          <MultiCropPicker cropMix={cropMix} onChange={setCropMix} lang={lang} CROPS={CROPS} tCrop={tCrop} />
        )}
        <div style={{ marginTop: 8, position: 'relative' }}>
          {analysisDate === new Date().toISOString().slice(0, 10) && (
            <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', fontSize:13, color:'#1a2e1a', pointerEvents:'none', zIndex:1 }}>Today</span>
          )}
          <input
            type="date"
            value={analysisDate}
            min="2015-06-23"
            max={new Date().toISOString().slice(0, 10)}
            onChange={e => setAnalysisDate(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 7, border: '1px solid #c8d8c0', background: '#f5f9f3', color: analysisDate === new Date().toISOString().slice(0, 10) ? 'transparent' : '#1a2e1a', boxSizing: 'border-box', cursor: 'pointer' }}
          />
        </div>
        {coordinates && (
          <button
            id="mobile-analyze-btn"
            onClick={analyzeField}
            disabled={loading}
            style={{
              marginTop: 10,
              width: '100%',
              padding: '11px 16px',
              fontSize: 14,
              fontWeight: 600,
              background: loading ? '#7aab5a' : '#2d5a1b',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: loading ? 'default' : 'pointer',
              transition: 'background 0.2s',
            }}>
            {loading ? (areaMode ? t("analyzingArea") : t("analyzing")) : areaMode ? (areaResult ? t("reanalyzeArea") : t("analyzeArea")) : result ? t("reanalyze") : t("analyze")}
          </button>
        )}
      </div>

      {coordinates && (() => {
        const ha = computeAreaHa(coordinates);
        return (
          <div style={{ ...s.card, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div>
              <div style={{ fontSize:9, fontWeight:700, color:"#999", textTransform:"uppercase", letterSpacing:1, marginBottom:2 }}>Field Size</div>
              <div style={{ fontSize:18, fontWeight:700, color:"#1a2e1a" }}>{formatArea(ha, areaUnit)}</div>
            </div>
            <select
              value={areaUnit}
              onChange={e => setAreaUnit(e.target.value)}
              style={{ fontSize:11, border:"0.5px solid #ddd", borderRadius:6, padding:"4px 6px", background:"#fafaf8", color:"#555", cursor:"pointer" }}>
              <option value="ha">Hectares (ha)</option>
              <option value="ac">Acres (ac)</option>
              <option value="sqkm">km²</option>
              <option value="sqm">m²</option>
            </select>
          </div>
        );
      })()}

      {result && (
        <div style={{ ...s.card, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <span style={{ fontSize:13, color:"#444" }}>{t("ndviOverlay")}</span>
          <button onClick={()=>{ const n=!showNdvi; setShowNdvi(n); setNdviTileUrl(n?result.ndvi_tile_url:null); }}
            style={{ ...s.smallBtn, ...(showNdvi?s.smallBtnActive:{}) }}>
            {showNdvi?t("on"):t("off")}
          </button>
        </div>
      )}



      {error && <div style={s.error}>{error}</div>}

      {areaResult && <AreaAnalysisPanel data={areaResult} lang={lang} />}

      {result && <>

        {/* Field overview */}
        <div style={{ ...s.card, borderLeft:`3px solid ${healthColor(result.health_status)}` }}>
          <div style={s.cardLabel}>{t("fieldOverview")}</div>
          <div style={{ display:"flex", gap:12 }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:22, fontWeight:600, color:healthColor(result.health_status) }}>{result.ndvi}</div>
              <div style={s.lbl}>NDVI</div>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:22, fontWeight:600, color:result.ndvi_change>=0?"#639922":"#E24B4A" }}>
                {result.ndvi_change>=0?"+":""}{result.ndvi_change}
              </div>
              <div style={s.lbl}>3-mo trend</div>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ display:"inline-block", background:healthColor(result.health_status), color:"#fff", fontSize:10, fontWeight:600, padding:"3px 8px", borderRadius:20 }}>{result.health_status}</div>
              <div style={s.lbl}>Status</div>
            </div>
          </div>
        </div>

        {/* Save analysis — shown whenever user is logged in */}
        {embedUser && (drawnPolygon || coordinates) && (
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <button onClick={() => { setSaveModalOpen(true); setSaveMsg(null); }}
              style={{ flex:1, padding:"9px 0", fontSize:12, fontWeight:700, background:"#2d5a1b", color:"#fff", border:"none", borderRadius:8, cursor:"pointer" }}>
              {t("saveAnalysisBtn")}
            </button>
            {saveMsg && <span style={{ fontSize:12, color:"#27ae60", fontWeight:600 }}>{saveMsg}</span>}
          </div>
        )}

        {/* Save modal */}
        {saveModalOpen && (
          <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <div style={{ background:"#fff", borderRadius:12, padding:"24px 28px", width:320, boxShadow:"0 8px 40px rgba(0,0,0,0.2)" }}>
              <div style={{ fontSize:15, fontWeight:700, color:"#1a2e1a", marginBottom:14 }}>{t("saveAnalysisBtn")}</div>
              <label style={{ fontSize:12, fontWeight:600, color:"#7a9a6a", display:"block", marginBottom:4 }}>{t("saveAnalysisName")}</label>
              <input value={saveName} onChange={e=>setSaveName(e.target.value)}
                placeholder="e.g. North field — July 2025"
                style={{ width:"100%", padding:"8px 10px", fontSize:13, border:"1px solid #c8d8c0", borderRadius:7, marginBottom:12, boxSizing:"border-box" }}
                autoFocus onKeyDown={e=>{ if(e.key==="Enter") saveAnalysis(); }} />
              {embedUser?.orgs?.length > 1 && (
                <>
                  <label style={{ fontSize:12, fontWeight:600, color:"#7a9a6a", display:"block", marginBottom:4 }}>Organization</label>
                  <select value={saveOrgId} onChange={e=>setSaveOrgId(Number(e.target.value))}
                    style={{ width:"100%", padding:"8px 10px", fontSize:13, border:"1px solid #c8d8c0", borderRadius:7, marginBottom:12, boxSizing:"border-box" }}>
                    {embedUser.orgs.map(o=><option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                </>
              )}
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={saveAnalysis} disabled={savingAnalysis || !saveName.trim()}
                  style={{ flex:1, padding:"9px 0", fontSize:13, fontWeight:700, background:"#2d5a1b", color:"#fff", border:"none", borderRadius:8, cursor:"pointer", opacity: saveName.trim() ? 1 : 0.5 }}>
                  {savingAnalysis ? t("savingBtn") : t("saveBtn")}
                </button>
                <button onClick={()=>setSaveModalOpen(false)}
                  style={{ flex:1, padding:"9px 0", fontSize:13, fontWeight:600, background:"#f0f6ea", color:"#2d5a1b", border:"1px solid #c8d8c0", borderRadius:8, cursor:"pointer" }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Weather */}
        {result.weather && !result.weather.error && (
          <div style={s.card}>
            <div style={s.cardLabel}>7-day weather context</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:6, marginBottom:8 }}>
              {[
                ["Avg temp",  `${result.weather.avg_temp_7d}°C`,         result.weather.avg_temp_7d>34?"#E24B4A":"#1a2e1a"],
                ["Max temp",  `${result.weather.max_temp_7d}°C`,         result.weather.max_temp_7d>36?"#E24B4A":"#1a2e1a"],
                ["Rainfall",  `${result.weather.total_precip_7d_mm}mm`,  result.weather.total_precip_7d_mm<2?"#E24B4A":"#2196F3"],
                ["Humidity",  `${result.weather.avg_rh_7d_pct}%`,        result.weather.avg_rh_7d_pct>85?"#E24B4A":"#1a2e1a"],
                ["Dry days",  `${result.weather.consecutive_dry_days}d`, result.weather.consecutive_dry_days>10?"#E24B4A":result.weather.consecutive_dry_days>5?"#EF9F27":"#639922"],
                ["ET₀ 7d",    `${result.weather.et0_7d_mm}mm`,           "#1a2e1a"],
              ].map(([l,v,c])=>(
                <div key={l} style={{ background:"#fafaf8", borderRadius:8, padding:"8px", border:"0.5px solid #eee" }}>
                  <div style={{ fontSize:9, color:"#999", textTransform:"uppercase", letterSpacing:1 }}>{l}</div>
                  <div style={{ fontSize:15, fontWeight:700, color:c, marginTop:2 }}>{v}</div>
                </div>
              ))}
            </div>
            {result.weather.precip_anomaly_pct!=null && (
              <div style={{ fontSize:11, color:result.weather.precip_anomaly_pct<-50?"#E24B4A":result.weather.precip_anomaly_pct>100?"#2196F3":"#666", padding:"5px 8px", background:"#fafaf8", borderRadius:6 }}>
                Rainfall is <strong>{Math.abs(result.weather.precip_anomaly_pct)}% {result.weather.precip_anomaly_pct<0?"below":"above"} normal</strong> (normal: {result.weather.weekly_normal_mm}mm/wk)
              </div>
            )}
          </div>
        )}

        {/* NDVI historical trend chart */}
        {result.ndvi_series && result.ndvi_series.length > 1 && (() => {
          const series = result.ndvi_series;
          const vals = series.map(p => p.ndvi);
          const minV = Math.min(...vals); const maxV = Math.max(...vals);
          const range = maxV - minV || 0.01;
          const W = 300; const H = 90; const PAD = 6;
          const xs = series.map((_,i) => PAD + (i / (series.length - 1)) * (W - PAD * 2));
          const ys = vals.map(v => H - PAD - ((v - minV) / range) * (H - PAD * 2));
          const pts = xs.map((x, i) => `${x},${ys[i]}`).join(" ");
          const fillPts = `${xs[0]},${H - PAD} ` + pts + ` ${xs[xs.length-1]},${H - PAD}`;
          return (
            <div style={s.card}>
              <div style={s.cardLabel}>NDVI Trend — {result.analysis_date} to Today</div>
              <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height: 90, display:"block", overflow:"visible" }}>
                <polygon points={fillPts} fill="#639922" fillOpacity={0.15} />
                <polyline points={pts} fill="none" stroke="#2d5a1b" strokeWidth={1.8} strokeLinejoin="round" />
                {series.map((p, i) => (
                  <g key={i}>
                    <circle cx={xs[i]} cy={ys[i]} r={2.5} fill="#2d5a1b" />
                    {(i === 0 || i === series.length - 1 || series.length <= 8) && (
                      <text x={xs[i]} y={ys[i] - 5} textAnchor="middle" fontSize={7} fill="#555">{p.date}</text>
                    )}
                  </g>
                ))}
                <text x={PAD} y={PAD + 4} fontSize={7} fill="#999">{maxV.toFixed(2)}</text>
                <text x={PAD} y={H - PAD - 1} fontSize={7} fill="#999">{minV.toFixed(2)}</text>
              </svg>
            </div>
          );
        })()}

        {/* ECOSTRESS thermal */}
        {result.ecostress && result.ecostress.available && (
          <div style={s.card}>
            <div style={s.cardLabel}>Thermal Analysis — Landsat 8/9 + MODIS ET</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
              {result.ecostress.lst_c != null && (
                <div style={{ background:"#fafaf8", borderRadius:8, padding:"10px", border:"0.5px solid #eee" }}>
                  <div style={{ fontSize:9, color:"#999", textTransform:"uppercase", letterSpacing:1 }}>Land Surface Temp</div>
                  <div style={{ fontSize:20, fontWeight:700, marginTop:2,
                    color: result.ecostress.lst_c > 45 ? "#E24B4A"
                         : result.ecostress.lst_c > 38 ? "#EF9F27"
                         : result.ecostress.lst_c < 5  ? "#2196F3"
                         : "#639922" }}>
                    {result.ecostress.lst_c}°C
                  </div>
                  <div style={{ fontSize:10, color:"#888", marginTop:2 }}>{result.ecostress.lst_status}</div>
                </div>
              )}
              {result.ecostress.et_mm_day != null && (
                <div style={{ background:"#fafaf8", borderRadius:8, padding:"10px", border:"0.5px solid #eee" }}>
                  <div style={{ fontSize:9, color:"#999", textTransform:"uppercase", letterSpacing:1 }}>Actual ET</div>
                  <div style={{ fontSize:20, fontWeight:700, color:"#2196F3", marginTop:2 }}>
                    {result.ecostress.et_mm_day}mm/d
                  </div>
                  <div style={{ fontSize:10, color:"#888", marginTop:2 }}>{result.ecostress.et_status}</div>
                </div>
              )}
            </div>
            {result.ecostress.esi != null && (
              <div style={{ padding:"8px 10px", background:"#fafaf8", borderRadius:8, border:"0.5px solid #eee" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                  <div style={{ fontSize:11, fontWeight:600, color:"#333" }}>Evaporative Stress Index</div>
                  <div style={{ fontSize:13, fontWeight:700,
                    color: result.ecostress.esi < 0.3 ? "#E24B4A"
                         : result.ecostress.esi < 0.5 ? "#EF9F27"
                         : "#639922" }}>
                    {result.ecostress.esi}
                  </div>
                </div>
                <div style={{ height:6, background:"#eee", borderRadius:3, overflow:"hidden" }}>
                  <div style={{ height:"100%", borderRadius:3,
                    background: result.ecostress.esi < 0.3 ? "#E24B4A"
                              : result.ecostress.esi < 0.5 ? "#EF9F27"
                              : "#639922",
                    width:`${Math.min(100, result.ecostress.esi * 100)}%` }}/>
                </div>
                <div style={{ fontSize:10, color:"#888", marginTop:4 }}>
                  Crop transpiring at {Math.round(result.ecostress.esi * 100)}% of potential — 0 = full stress, 1 = no stress
                </div>
              </div>
            )}
            {result.ecostress.date && (
              <div style={{ fontSize:10, color:"#aaa", marginTop:8 }}>
                Landsat overpass: {result.ecostress.date} · 100m LST · 500m ET (MODIS MOD16A2 8-day)
              </div>
            )}
          </div>
        )}

        {/* NISAR Soil Moisture */}
        <div style={s.card}>
          <div style={s.cardLabel}>Soil Moisture — NISAR L2 (200m)</div>
          {result.nisar_available ? (
            <>
              <div style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:6 }}>
                <div style={{ fontSize:26, fontWeight:700,
                  color: result.nisar_sm_vol < 0.10 ? "#E24B4A"
                       : result.nisar_sm_vol < 0.20 ? "#EF9F27"
                       : result.nisar_sm_vol >= 0.45 ? "#2196F3"
                       : "#639922" }}>
                  {(result.nisar_sm_vol * 100).toFixed(1)}%
                </div>
                <div style={{ fontSize:11, color:"#888" }}>vol. water content (m³/m³)</div>
              </div>
              <div style={{ height:6, background:"#eee", borderRadius:3, overflow:"hidden", marginBottom:6 }}>
                <div style={{ height:"100%", borderRadius:3,
                  background: result.nisar_sm_vol < 0.10 ? "#E24B4A"
                            : result.nisar_sm_vol < 0.20 ? "#EF9F27"
                            : result.nisar_sm_vol >= 0.45 ? "#2196F3"
                            : "#639922",
                  width:`${Math.min(100, result.nisar_sm_vol / 0.50 * 100)}%` }}/>
              </div>
              <div style={{ fontSize:11, color:"#555", marginBottom:4 }}>{result.nisar_sm_status}</div>
              {result.nisar_date && (
                <div style={{ fontSize:10, color:"#aaa" }}>NISAR overpass: {result.nisar_date} · 200m resolution</div>
              )}
            </>
          ) : (
            <div style={{ fontSize:12, color:"#aaa", padding:"8px 0" }}>
              {result.nisar_sm_status || "NISAR data not yet available for this location"}
            </div>
          )}
        </div>

        {/* Yield Risk Index */}
        {result.yri && (
          <div style={{ ...s.card, borderLeft:`4px solid ${result.yri.color}` }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
              <div style={s.cardLabel}>Yield Risk Index</div>
              <span style={{ fontSize:9, background:"#fff3cd", color:"#856404", padding:"2px 7px", borderRadius:10, fontWeight:600, border:"1px solid #ffc107" }}>
                EXPERIMENTAL
              </span>
            </div>
            <div style={{ fontSize:10, color:"#999", marginBottom:10, fontStyle:"italic" }}>
              Remote-sensing composite (NDVI, water, heat, nutrients, soil). Use as one input among many — not a yield guarantee.
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:12 }}>
              <div style={{ fontSize:48, fontWeight:800, color:result.yri.color, lineHeight:1 }}>
                {result.yri.yri}
              </div>
              <div>
                <div style={{ fontSize:14, fontWeight:700, color:result.yri.color }}>
                  {result.yri.label}
                </div>
                <div style={{ fontSize:11, color:"#888", marginTop:2 }}>
                  out of 100 — lower is better
                </div>
              </div>
            </div>
            <div style={{ marginBottom:8 }}>
              {Object.entries(result.yri.components).map(([key, val]) => {
                const labels = {
                  vegetation_stress: "Vegetation",
                  water_stress:      "Water",
                  heat_stress:       "Heat",
                  nutrient_stress:   "Nutrients",
                  disease_pressure:  "Disease/Pest",
                  soil_constraints:  "Soil",
                  trend_penalty:     "Trend",
                };
                const colors = {
                  vegetation_stress: "#639922",
                  water_stress:      "#2196F3",
                  heat_stress:       "#E24B4A",
                  nutrient_stress:   "#EF9F27",
                  disease_pressure:  "#9C27B0",
                  soil_constraints:  "#8B6914",
                  trend_penalty:     "#888",
                };
                return (
                  <div key={key} style={{ marginBottom:5 }}>
                    <div style={{ display:"flex", justifyContent:"space-between",
                      fontSize:10, color:"#666", marginBottom:2 }}>
                      <span>{labels[key]}</span>
                      <span style={{ fontWeight:600, color:colors[key] }}>{val}</span>
                    </div>
                    <div style={{ height:5, background:"#eee", borderRadius:3, overflow:"hidden" }}>
                      <div style={{ height:"100%", borderRadius:3,
                        background:colors[key],
                        width:`${Math.min(val, 100)}%`,
                        opacity: val > 0 ? 1 : 0.2 }}/>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize:10, color:"#aaa" }}>
              Composite of NDVI, NDMI, LST, ESI, NDRE, soil pH/SOC, disease rules, and NDVI trend
            </div>

            {/* Per-crop breakdown when intercropping */}
            {result.per_crop_yri && result.per_crop_yri.length > 1 && (
              <div style={{ marginTop:12, borderTop:"1px solid #f0f0f0", paddingTop:10 }}>
                <div style={{ fontSize:11, fontWeight:700, color:"#5a7a4a", marginBottom:8, textTransform:"uppercase", letterSpacing:0.5 }}>{t("perCropRiskBreakdown")}</div>
                {result.per_crop_yri.map((c, i) => (
                  <div key={i} style={{ marginBottom:12, paddingBottom:10, borderBottom: i < result.per_crop_yri.length-1 ? "1px solid #f5f5f0" : "none" }}>
                    {/* Crop header row */}
                    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom: c.crop_specific_issues?.length ? 6 : 0 }}>
                      <div style={{ width:8, height:8, borderRadius:"50%", background:MIX_COLORS[i], flexShrink:0 }} />
                      <div style={{ flex:1, fontSize:12, fontWeight:700, color:"#1a2e1a", textTransform:"capitalize" }}>{tCrop(c.crop, lang)}</div>
                      <div style={{ fontSize:11, color:"#888" }}>{c.pct}%</div>
                      <div style={{ background:c.color, color:"#fff", borderRadius:6, padding:"2px 8px", fontSize:11, fontWeight:700, flexShrink:0 }}>
                        {c.label} ({c.yri})
                      </div>
                    </div>
                    {/* Crop-specific issues */}
                    {c.crop_specific_issues && c.crop_specific_issues.length > 0 && (
                      <div style={{ marginLeft:18 }}>
                        {c.crop_specific_issues.map((issue, j) => (
                          <div key={j} style={{ display:"flex", gap:6, alignItems:"flex-start", marginTop:4 }}>
                            <span style={{ color:"#e65100", fontSize:11, flexShrink:0, marginTop:1 }}>▲</span>
                            <span style={{ fontSize:11, color:"#374151", lineHeight:1.5 }}>{issue}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {c.crop_specific_issues && c.crop_specific_issues.length === 0 && c.yri <= 25 && (
                      <div style={{ marginLeft:18, fontSize:11, color:"#16a34a", marginTop:3 }}>No crop-specific concerns at current conditions.</div>
                    )}
                  </div>
                ))}
                <div style={{ fontSize:10, color:"#aaa", marginTop:2 }}>{t("outOf100")} — {t("compositeWeightedNote")}</div>
              </div>
            )}
          </div>
        )}

        {/* Yield & Revenue Estimate */}
        {result.yri && (() => {
          const areaHa = result.area_ha || computeAreaHa(coordinates);
          if (!areaHa) return null;
          const ndvi = result.ndvi || 0.5;
          const yriPenaltyFactor = 1 - (result.yri.yri / 100) * 0.7;
          // Attainable yield ceilings (t/ha) from FAO GAEZ v4 (2021) and
          // FAO FAOSTAT world average / irrigated-best figures:
          //   Corn: FAO GAEZ irrigated attainable ~10 t/ha (GAEZ v4, Table 4.1)
          //   Wheat: FAO world record irrigated ~8 t/ha (GAEZ v4); typical attainable ~7
          //   Rice: IRRI attainable ~8–9 t/ha (Peng et al. 2000, Field Crops Res.)
          //   Soybean: yield contest records ~4–5; FAO attainable ~3.5 (GAEZ v4)
          //   Tomato: FAO FAOSTAT world high ~80–100 t/ha; typical greenhouse ~70
          //   Potato: FAO FAOSTAT high-input irrigated ~45–50; attainable ~40
          //   Sugarcane: FAO GAEZ irrigated ~80 t/ha fresh weight
          //   Cotton lint: FAO GAEZ ~2 t/ha lint
          //   Sunflower: FAO GAEZ irrigated ~3.5 t/ha
          //   Barley: FAO GAEZ irrigated ~6 t/ha; typical ~5
          //   Sorghum: FAO GAEZ attainable ~5 t/ha
          //   Cassava: IITA attainable ~25–30 t/ha fresh root
          const cropsMaxYield = {
            "Corn / Maize":10, "Wheat (winter)":7, "Rice (paddy)":8.5, "Soybean":3.5,
            "Tomato (fresh)":70, "Potato":40, "Sugarcane":80, "Cotton":2,
            "Sunflower":3.5, "Barley":5, "Sorghum":5, "Cassava":25
          };
          const maxYield = cropsMaxYield[crop] || 5;
          // NDVI–yield model: linear rescaling from NDVI range [0.1, 0.7] → [10%, 100%]
          // of attainable yield, following Becker-Reshef et al. (2010) Remote Sens. Environ.
          // and validated in Lobell et al. (2015) Nature Plants.
          const ndviYield = maxYield * Math.min(1, Math.max(0.1, (ndvi - 0.1) / 0.6));
          const estimatedYield = Math.round(ndviYield * yriPenaltyFactor * 10) / 10;
          const totalTons = Math.round(estimatedYield * areaHa * 10) / 10;
          const revenue = Math.round(totalTons * yriPriceKg * 1000);
          return (
            <div style={{ ...s.card, borderLeft:"4px solid #2e7d32" }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                <div style={s.cardLabel}>Yield & Revenue Estimate</div>
                <span style={{ fontSize:9, background:"#fff3cd", color:"#856404", padding:"2px 7px", borderRadius:10, fontWeight:600, border:"1px solid #ffc107" }}>
                  EXPERIMENTAL
                </span>
              </div>
              <div style={{ fontSize:10, color:"#999", marginBottom:10, fontStyle:"italic" }}>
                Indicative only. NDVI–yield regression (Becker-Reshef et al. 2010; Lobell et al. 2015). Yield ceilings: FAO GAEZ v4 (2021) irrigated-attainable figures.
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:12 }}>
                <div style={{ textAlign:"center", background:"#f5f9f0", borderRadius:8, padding:10 }}>
                  <div style={{ fontSize:20, fontWeight:700, color:"#2e7d32" }}>{estimatedYield}</div>
                  <div style={{ fontSize:9, color:"#888" }}>t/ha estimated</div>
                </div>
                <div style={{ textAlign:"center", background:"#f5f9f0", borderRadius:8, padding:10 }}>
                  <div style={{ fontSize:20, fontWeight:700, color:"#2e7d32" }}>{totalTons}</div>
                  <div style={{ fontSize:9, color:"#888" }}>total tons ({Math.round(areaHa*10)/10} ha)</div>
                </div>
                <div style={{ textAlign:"center", background:"#f0f7ff", borderRadius:8, padding:10 }}>
                  <div style={{ fontSize:20, fontWeight:700, color:"#1565c0" }}>${revenue.toLocaleString()}</div>
                  <div style={{ fontSize:9, color:"#888" }}>est. revenue</div>
                </div>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                <label style={{ fontSize:11, color:"#666", flexShrink:0 }}>Market price ($/kg):</label>
                <input type="number" min="0.01" step="0.05" value={yriPriceKg}
                  onChange={e => setYriPriceKg(parseFloat(e.target.value)||0.5)}
                  style={{ width:70, padding:"3px 6px", border:"1px solid #ccc", borderRadius:5, fontSize:12 }} />
              </div>
              <div style={{ fontSize:9, color:"#aaa", borderTop:"1px solid #eee", paddingTop:6 }}>
                Sources: Yield ceilings — FAO GAEZ v4 (2021). NDVI–yield model — Becker-Reshef et al. (2010) <em>Remote Sens. Environ.</em> 114(3); Lobell et al. (2015) <em>Nature Plants</em> 1, 15001.
              </div>
            </div>
          );
        })()}

        {/* Spectral indices */}
        <div style={s.card}>
          <div style={s.cardLabel}>{t("spectral")}</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
            {result.indices && Object.entries(result.indices).map(([key,idx])=>{
              const colMap = { NDVI:healthColor(result.health_status), EVI:healthColor(result.health_status), MSAVI:"#639922", GNDVI:"#2d8a4e", NDMI:ndmiColor(result.ndmi), NDWI:"#2196F3", NDRE:ndreColor(result.nitrogen), ReCI:ndreColor(result.nitrogen), GCI:ndreColor(result.nitrogen), PSRI:(idx.mean||0)>0.1?"#E24B4A":"#888" };
              const color = colMap[key]||"#888";
              // Range chart: map p10→p90 range onto 0–1 based on known index ranges
              const RANGES = { NDVI:[-0.2,1], EVI:[-0.2,1], MSAVI:[-0.2,1], GNDVI:[-0.2,1], NDMI:[-1,1], NDWI:[-1,1], NDRE:[-0.5,0.8], ReCI:[0,10], GCI:[0,10], PSRI:[-1,1] };
              const [lo, hi] = RANGES[key] || [-1, 1];
              const span = hi - lo;
              const toP = v => Math.max(0, Math.min(100, ((v - lo) / span) * 100));
              const hasRange = idx.p10 != null && idx.p90 != null && idx.mean != null;
              const p10pct = hasRange ? toP(idx.p10) : null;
              const p90pct = hasRange ? toP(idx.p90) : null;
              const meanPct = hasRange ? toP(idx.mean) : null;
              return (
                <div key={key} style={{ background:"#fafaf8", borderRadius:8, padding:"10px", border:"0.5px solid #eee" }}>
                  <div style={{ display:"flex", justifyContent:"space-between" }}>
                    <div style={{ fontSize:9, fontWeight:700, color:"#999", textTransform:"uppercase", letterSpacing:1 }}>{key}</div>
                    <div style={{ fontSize:9, color:"#aaa" }}>{idx.label}</div>
                  </div>
                  <div style={{ fontSize:16, fontWeight:700, color, margin:"4px 0 2px" }}>{idx.mean??"—"}</div>
                  {/* Range bar */}
                  {hasRange ? (
                    <div style={{ position:"relative", height:6, background:"#eee", borderRadius:3, marginTop:6, overflow:"visible" }}>
                      {/* p10–p90 band */}
                      <div style={{ position:"absolute", left:`${p10pct}%`, width:`${p90pct-p10pct}%`, height:"100%", background:color, opacity:0.25, borderRadius:3 }}/>
                      {/* mean marker */}
                      <div style={{ position:"absolute", left:`${meanPct}%`, top:-2, width:3, height:10, background:color, borderRadius:2, transform:"translateX(-50%)" }}/>
                    </div>
                  ) : (
                    <div style={{ height:6, background:"#eee", borderRadius:3, marginTop:6 }}/>
                  )}
                  {hasRange && (
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:8, color:"#bbb", marginTop:3 }}>
                      <span>{idx.p10}</span><span>p10 ← mean → p90</span><span>{idx.p90}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Spectral signature curve — shown when spectral_bands data is present */}
        {result.spectral_bands && (() => {
          const BAND_COLORS_A = { B2:"#4a90d9", B3:"#27ae60", B4:"#e74c3c", B5:"#c0392b", B6:"#e67e22", B7:"#d35400", B8:"#8e44ad", B8A:"#6c3483", B11:"#7f8c8d" };
          const order = ["B2","B3","B4","B5","B6","B7","B8","B8A","B11"];
          const pts = order.map(k => result.spectral_bands[k]).filter(p => p && p.mean != null);
          if (!pts.length) return null;
          const W=520,H=180,PAD={t:12,r:12,b:50,l:46};
          const cW=W-PAD.l-PAD.r, cH=H-PAD.t-PAD.b;
          const maxV = Math.max(...pts.map(p=>p.mean),0.05);
          const minNm=pts[0].nm, maxNm=pts[pts.length-1].nm;
          const x=nm=>PAD.l+((nm-minNm)/(maxNm-minNm))*cW;
          const y=v=>PAD.t+cH-((v/(maxV*1.1))*cH);
          const path=pts.map((p,i)=>`${i===0?"M":"L"}${x(p.nm).toFixed(1)},${y(p.mean).toFixed(1)}`).join(" ");
          const area=`${path} L${x(pts[pts.length-1].nm)},${(PAD.t+cH).toFixed(1)} L${x(pts[0].nm)},${(PAD.t+cH).toFixed(1)} Z`;
          return (
            <div style={s.card}>
              <div style={s.cardLabel}>Spectral Signature Curve</div>
              <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", display:"block" }}>
                {[0,maxV*0.33,maxV*0.66,maxV].map(v=>(
                  <g key={v}>
                    <line x1={PAD.l} x2={W-PAD.r} y1={y(v)} y2={y(v)} stroke="#eee" strokeWidth="1"/>
                    <text x={PAD.l-4} y={y(v)+3} textAnchor="end" fontSize="8" fill="#aaa">{v.toFixed(3)}</text>
                  </g>
                ))}
                <path d={area} fill="rgba(45,90,27,0.07)"/>
                <path d={path} fill="none" stroke="#2d5a1b" strokeWidth="2" strokeLinejoin="round"/>
                {pts.map((p,i)=>{
                  const k=order.find(k=>result.spectral_bands[k]===p);
                  const even = i%2===0;
                  const labelY = even ? PAD.t+cH+13 : PAD.t+cH+25;
                  const nmY    = even ? PAD.t+cH+22 : PAD.t+cH+34;
                  return (
                    <g key={p.label}>
                      {/* tick line to label */}
                      <line x1={x(p.nm)} x2={x(p.nm)} y1={PAD.t+cH} y2={PAD.t+cH+(even?8:20)} stroke="#ddd" strokeWidth="1"/>
                      <circle cx={x(p.nm)} cy={y(p.mean)} r="3.5" fill={BAND_COLORS_A[k]||"#2d5a1b"} stroke="#fff" strokeWidth="1.5"/>
                      <text x={x(p.nm)} y={labelY} textAnchor="middle" fontSize="8" fill="#7a9a6a">{p.label}</text>
                      <text x={x(p.nm)} y={nmY}    textAnchor="middle" fontSize="7" fill="#bbb">{p.nm}nm</text>
                    </g>
                  );
                })}
                <line x1={PAD.l} x2={PAD.l} y1={PAD.t} y2={PAD.t+cH} stroke="#ddd" strokeWidth="1"/>
                <line x1={PAD.l} x2={W-PAD.r} y1={PAD.t+cH} y2={PAD.t+cH} stroke="#ddd" strokeWidth="1"/>
              </svg>
              <div style={{ display:"flex", flexWrap:"wrap", gap:"4px 12px", marginTop:6 }}>
                {pts.map(p=>{
                  const k=order.find(k=>result.spectral_bands[k]===p);
                  return <span key={k} style={{ fontSize:10, color:"#555" }}><span style={{ display:"inline-block", width:7, height:7, borderRadius:"50%", background:BAND_COLORS_A[k], marginRight:3, verticalAlign:"middle" }}/>{k} {p.mean}</span>;
                })}
              </div>
            </div>
          );
        })()}

        {/* Zone breakdown */}
        {result.zones && (
          <div style={s.card}>
            <div style={s.cardLabel}>{t("zoneBreakdown")}</div>
            <div style={{ display:"flex", height:14, borderRadius:7, overflow:"hidden", marginBottom:6 }}>
              {result.zones.healthy_pct>0  && <div style={{ width:`${result.zones.healthy_pct}%`,  background:"#639922" }}/>}
              {result.zones.fair_pct>0     && <div style={{ width:`${result.zones.fair_pct}%`,    background:"#EF9F27" }}/>}
              {result.zones.stressed_pct>0 && <div style={{ width:`${result.zones.stressed_pct}%`,background:"#E8830A" }}/>}
              {result.zones.severe_pct>0   && <div style={{ width:`${result.zones.severe_pct}%`,  background:"#E24B4A" }}/>}
            </div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {[["#639922",t("healthy"),result.zones.healthy_pct],["#EF9F27",t("fair"),result.zones.fair_pct],["#E8830A",t("stressed"),result.zones.stressed_pct],["#E24B4A",t("severe"),result.zones.severe_pct]].map(([col,lbl,pct])=>(
                <div key={lbl} style={{ display:"flex", alignItems:"center", gap:4 }}>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:col }}/>
                  <span style={{ fontSize:10, color:"#666" }}>{lbl} {pct}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Soil — error state */}
        {result.soil && result.soil.error && (
          <div style={s.card}>
            <div style={s.cardLabel}>Soil Profile</div>
            <div style={{ fontSize:12, color:"#888", padding:"8px 0" }}>⚠️ {result.soil.error}</div>
          </div>
        )}

        {/* Soil */}
        {result.soil && !result.soil.error && (
          <div style={s.card}>
            <div style={s.cardLabel}>{t("soilProfile")}</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
              <div style={{ background:"#fafaf8", borderRadius:8, padding:"8px 6px", border:"0.5px solid #eee" }}>
                <div style={{ fontSize:9, fontWeight:700, color:"#999", textTransform:"uppercase", letterSpacing:1 }}>{t("clay")}</div>
                <div style={{ fontSize:18, fontWeight:700, color:"#8B6914", margin:"2px 0" }}>{result.soil.clay_pct}%</div>
                <div style={{ fontSize:9, color:"#888" }}>Sand {result.soil.sand_pct}%</div>
              </div>
              <div style={{ background:"#fafaf8", borderRadius:8, padding:"8px 6px", border:"0.5px solid #eee" }}>
                <div style={{ fontSize:9, fontWeight:700, color:"#999", textTransform:"uppercase", letterSpacing:1 }}>{t("ph")}</div>
                <div style={{ fontSize:18, fontWeight:700, color:result.soil.ph<5.5?"#E24B4A":result.soil.ph>7.5?"#EF9F27":"#639922", margin:"2px 0" }}>{result.soil.ph}</div>
                <div style={{ fontSize:9, color:"#888" }}>{result.soil.ph<5.5?"Acidic":result.soil.ph>7.5?"Alkaline":"Neutral"}</div>
              </div>
              <div style={{ background:"#fafaf8", borderRadius:8, padding:"8px 6px", border:"0.5px solid #eee" }}>
                <div style={{ fontSize:9, fontWeight:700, color:"#999", textTransform:"uppercase", letterSpacing:1 }}>{t("carbon")}</div>
                <div style={{ fontSize:18, fontWeight:700, color:result.soil.soc_g_kg<8?"#E24B4A":"#639922", margin:"2px 0" }}>{result.soil.soc_g_kg}</div>
                <div style={{ fontSize:9, color:"#888" }}>g/kg SOC</div>
              </div>
            </div>
            <div style={{ marginTop:8, padding:"6px 8px", background:"#fafaf8", borderRadius:6, fontSize:11, color:"#555", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span><strong>{result.soil.texture_class}</strong> · {result.soil.drainage}</span>
              {result.soil.soil_resolution && (
                <span style={{ fontSize:10, fontWeight:600, color:"#fff", background:result.soil.soil_resolution==="30m"?"#2d5a1b":"#888", padding:"2px 7px", borderRadius:20 }}>{result.soil.soil_resolution}</span>
              )}
            </div>
          </div>
        )}

        {/* Soil recommendations */}
        {result.soil_recommendations && result.soil_recommendations.length>0 && (
          <div style={s.card}>
            <div style={s.cardLabel}>Soil-based recommendations</div>
            {result.soil_recommendations.map((rec,i)=>{
              const col={ High:"#E24B4A", Medium:"#EF9F27", None:"#639922", Low:"#2196F3" }[rec.priority]||"#888";
              const bg={ High:"#FEF0F0", Medium:"#FFFBF0", None:"#F0FBF0", Low:"#F0F8FF" }[rec.priority]||"#fafaf8";
              return (
                <div key={i} style={{ marginBottom:10, padding:"10px 12px", background:bg, borderRadius:8, border:`1px solid ${col}30` }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:4, gap:8 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:"#333", flex:1 }}>{rec.recommendation}</div>
                    <div style={{ display:"flex", gap:4, flexShrink:0, flexWrap:"wrap", justifyContent:"flex-end" }}>
                      <span style={{ fontSize:9, fontWeight:600, color:col, background:`${col}20`, padding:"2px 6px", borderRadius:20 }}>{rec.priority}</span>
                      <span style={{ fontSize:9, color:"#aaa", background:"#f0f0e8", padding:"2px 6px", borderRadius:20 }}>{rec.category}</span>
                    </div>
                  </div>
                  <div style={{ fontSize:11, color:"#555", lineHeight:1.6 }}>{rec.detail}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Risks */}
        <div style={s.card}>
          <div style={s.cardLabel}>{t("riskFactors")}</div>
          {result.risks.length===0
            ? <div style={{ fontSize:12, color:"#3B6D11", background:"#EAF3DE", padding:"8px 10px", borderRadius:6 }}>{t("noRisks")}</div>
            : result.risks.map((r,i)=>(
              <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:10, padding:"8px 0", borderBottom:"0.5px solid #f0f0e8" }}>
                <div style={{ width:8, height:8, borderRadius:"50%", background:RISK_COLORS[r.level], marginTop:4, flexShrink:0 }}/>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:500 }}>{r.name}</div>
                  <div style={{ fontSize:11, color:"#888", marginTop:2 }}>{r.detail}</div>
                </div>
                <div style={{ fontSize:11, fontWeight:600, color:RISK_COLORS[r.level], flexShrink:0 }}>{r.level}</div>
              </div>
            ))
          }
        </div>

        {/* Actions */}
        {result.actions && result.actions.length > 0 && (
  <div style={s.card}>
    <div style={s.cardLabel}>{t("whatNext")}</div>
    {result.actions.map((a, i) => {
      const matchingRisk = result.risks[i];
      return (
        <div key={i} style={{ display:"flex", gap:10, alignItems:"flex-start", padding:"8px 0", borderBottom:"0.5px solid #f0f0e8" }}>
          <div style={{ width:20, height:20, borderRadius:"50%", background:"#e8f0e8",
            color:"#2d5a1b", fontSize:11, fontWeight:600, display:"flex",
            alignItems:"center", justifyContent:"center", flexShrink:0 }}>{i+1}</div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:13, fontWeight:500 }}>{a.action}</div>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:4 }}>
              <span style={{ fontSize:10, color:"#888" }}>{a.urgency}</span>
              {matchingRisk && (
                <span style={{ fontSize:10, fontWeight:600, color:RISK_COLORS[matchingRisk.level],
                  background:`${RISK_COLORS[matchingRisk.level]}18`,
                  padding:"1px 7px", borderRadius:20 }}>
                  {matchingRisk.name}
                </span>
              )}
            </div>
          </div>
        </div>
      );
    })}
  </div>
)}

        {/* Climate */}
        {result.climate && !result.climate.error && (()=>{
          const advice=getClimateAdvice(result.climate.koppen,crop);
          const cl=result.climate;
          const cropName=crop.charAt(0).toUpperCase()+crop.slice(1);
          const isGreat=advice.suitability==="Well suited"&&cl.ann_precip_mm>600&&cl.ann_temp_c>10&&cl.ann_temp_c<35;
          const risks={Af:"fungal disease and waterlogging",Am:"blast disease and waterlogging",Aw:"drought, fall armyworm, and aflatoxin",BSh:"drought and heat stress",BSk:"frost and short growing window",BWh:"extreme heat and water deficit",Cfa:"fungal disease in humid summers",Cfb:"late blight and cool summers",Csa:"summer drought and powdery mildew",Dfa:"spring flooding and rust",Dfb:"late frost and short seasons"}[cl.koppen]||"climate variability";
          return (
            <div style={{ ...s.card, borderLeft:`3px solid ${advice.suitColor}` }}>
              <div style={s.cardLabel}>{t("climateClass")}</div>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                <div style={{ background:"#1a2e1a", color:"#7AB648", fontWeight:700, fontSize:18, padding:"5px 12px", borderRadius:8, fontFamily:"monospace", flexShrink:0 }}>{cl.koppen}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:14, fontWeight:600, color:"#1a2e1a" }}>{cl.name}</div>
                  <div style={{ fontSize:11, color:"#888" }}>{cl.ann_temp_c}°C · {cl.ann_precip_mm}mm/yr</div>
                </div>
                <div style={{ background:advice.suitColor, color:"#fff", fontSize:10, fontWeight:600, padding:"3px 10px", borderRadius:20, flexShrink:0 }}>{advice.suitability}</div>
              </div>
              <div style={{ background:isGreat?"#EAF3DE":"#FFF8EE", border:`1px solid ${isGreat?"#C2DFA8":"#F5D9A0"}`, borderRadius:8, padding:"10px 12px", marginBottom:10, fontSize:12, color:"#1a2e1a", lineHeight:1.65 }}>
                <strong>You are growing {cropName} in a {cl.name} climate.</strong>{" "}
                {isGreat?`Great conditions for ${cropName}. Monitor for ${risks}.`:`Be careful of ${risks}. ${advice.suitability==="Challenging"?`${cropName} faces real challenges here.`:`${cropName} can succeed with good management.`}`}
              </div>
              <button onClick={()=>setClimateExpanded(p=>!p)}
                style={{ width:"100%", padding:"8px 0", background:"#f5f5f0", border:"0.5px solid #ddd", borderRadius:8, fontSize:12, fontWeight:600, color:"#2d5a1b", cursor:"pointer" }}>
                {climateExpanded?"Hide climate data":"See more climate data"}
              </button>
              {climateExpanded && (
                <div style={{ marginTop:10, display:"flex", flexDirection:"column", gap:8 }}>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                    {[["Ann. Avg Temp",`${cl.ann_temp_c}°C`,cl.ann_temp_c>30?"#E24B4A":"#639922"],["Ann. Rainfall",`${cl.ann_precip_mm}mm`,cl.ann_precip_mm<400?"#E24B4A":"#639922"],["Coolest Month",`${cl.min_month_temp}°C`,cl.min_month_temp<0?"#E24B4A":"#639922"],["Hottest Month",`${cl.max_month_temp}°C`,cl.max_month_temp>35?"#E24B4A":"#639922"]].map(([l,v,c])=>(
                      <div key={l} style={{ background:"#fafaf8", borderRadius:8, padding:"10px 12px", border:"0.5px solid #eee" }}>
                        <div style={{ fontSize:9, color:"#999", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{l}</div>
                        <div style={{ fontSize:20, fontWeight:700, color:c }}>{v}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ background:"#fafaf8", borderRadius:8, padding:"10px 12px", border:"0.5px solid #eee", fontSize:12, color:"#444", lineHeight:1.7 }}>
                    <div style={{ fontSize:10, fontWeight:700, color:"#999", textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>About this zone</div>
                    {advice.text}
                  </div>
                  {result.climate_matched_crops && result.climate_matched_crops.length > 0 && (
                  <div style={s.card}>
                    <div style={s.cardLabel}>Best-matched crops for your future conditions</div>
                    <div style={{ fontSize:11, color:"#888", marginBottom:10 }}>
                      {result.climate_matched_crops[0]?.zone_changed
                        ? `Scored against projected ${result.climate_matched_crops[0]?.future_zone} conditions by 2071` 
                        : `Scored against current ${result.climate?.koppen} conditions and your soil profile` 
                      }
                    </div>
                    {result.climate_matched_crops.map((c, i) => (
                      <div key={i} style={{ marginBottom:12, paddingBottom:12,
                        borderBottom: i < result.climate_matched_crops.length - 1
                          ? "0.5px solid #f0f0e8" : "none" }}>
                        <div style={{ display:"flex", justifyContent:"space-between",
                          alignItems:"center", marginBottom:6 }}>
                          <div style={{ fontSize:14, fontWeight:700, color:"#1a2e1a" }}>{tCrop(c.crop, lang)}</div>
                          <div style={{ fontSize:13, fontWeight:700,
                            color: c.score >= 75 ? "#639922" : c.score >= 55 ? "#EF9F27" : "#888",
                            background: c.score >= 75 ? "#EAF3DE" : c.score >= 55 ? "#FFF8EE" : "#f5f5f0",
                            padding:"3px 10px", borderRadius:20 }}>
                            {c.score}% match
                          </div>
                        </div>
                        {c.match_reasons.length > 0 && (
                          <div style={{ fontSize:11, color:"#2d5a1b", marginBottom:3 }}>
                            {c.match_reasons.join(" · ")}
                          </div>
                        )}
                        {c.match_warnings.length > 0 && (
                          <div style={{ fontSize:11, color:"#B85C00" }}>
                            {c.match_warnings.join(" · ")}
                          </div>
                        )}
                      </div>
                    ))}
                    <div style={{ fontSize:10, color:"#aaa", marginTop:4 }}>
                      Scored against perfect growing conditions in OpenFarm disease library
                    </div>
                  </div>
                )}

                {result.maxent_crops && result.maxent_crops.length > 0 && (
                  <div style={s.card}>
                    <div style={s.cardLabel}>Bioclimatic crop suitability — MaxEnt</div>
                    <div style={{ fontSize:11, color:"#888", marginBottom:10 }}>
                      Empirical species distribution models trained on global occurrence data.
                      Suitability scored 0–1 at this exact location.
                    </div>
                    {result.maxent_crops.map((c, i) => (
                      <div key={i} style={{ display:"flex", alignItems:"center", gap:10,
                        padding:"7px 0", borderBottom:"0.5px solid #f0f0e8" }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:13, fontWeight:600, color:"#1a2e1a" }}>{c.crop}</div>
                          <div style={{ fontSize:10, color:"#aaa", fontStyle:"italic" }}>{c.scientific}</div>
                        </div>
                        <div style={{ textAlign:"right", flexShrink:0 }}>
                          <div style={{ fontSize:13, fontWeight:700,
                            color: c.suitability >= 0.75 ? "#639922"
                                 : c.suitability >= 0.5  ? "#EF9F27" : "#888" }}>
                            {c.suitability_pct}%
                          </div>
                          <div style={{ fontSize:9, color: c.confidence_color, fontWeight:600 }}>
                            {c.model_confidence} model
                          </div>
                        </div>
                      </div>
                    ))}
                    <div style={{ fontSize:10, color:"#aaa", marginTop:8 }}>
                      MaxEnt SDMs — Beck et al. WorldClim bioclimatic variables
                    </div>
                  </div>
                )}
                  
                  {/* Climate trajectory */}
                  {cl.climate_shifting && (
                    <div style={{ marginTop:10, padding:"10px 12px", background:"#FFF8EE",
                      border:"1px solid #F5D9A0", borderRadius:8 }}>
                      <div style={{ fontSize:10, fontWeight:700, color:"#B85C00",
                        textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>
                        Climate trajectory
                      </div>
                      <div style={{ fontSize:12, color:"#333", marginBottom:10, lineHeight:1.6 }}>
                        Current zone: <strong>{cl.koppen} — {cl.name}</strong>
                      </div>
                      {/* Near future 2041–2070 */}
                      {cl.near_future && (
                        <>
                          <div style={{ fontSize:10, color:"#999", textTransform:"uppercase",
                            letterSpacing:1, marginBottom:6 }}>2041–2070 projections</div>
                          {Object.entries(cl.near_future).map(([ssp, data]) => (
                            <div key={ssp} style={{ display:"flex", justifyContent:"space-between",
                              alignItems:"center", marginBottom:5, gap:8 }}>
                              <span style={{ fontSize:11, color:"#666", flex:1 }}>{data.label}</span>
                              <span style={{ fontSize:11, fontWeight:700,
                                color: data.code === cl.koppen ? "#639922" : "#B85C00",
                                background: data.code === cl.koppen ? "#EAF3DE" : "#FFF0DC",
                                padding:"2px 8px", borderRadius:20, flexShrink:0, fontFamily:"monospace" }}>
                                {data.code}
                              </span>
                            </div>
                          ))}
                        </>
                      )}
                      {/* Far future 2071–2099 */}
                      {cl.far_future && (
                        <>
                          <div style={{ fontSize:10, color:"#999", textTransform:"uppercase",
                            letterSpacing:1, marginBottom:6, marginTop:10 }}>2071–2099 projections</div>
                          {Object.entries(cl.far_future).map(([ssp, data]) => (
                            <div key={ssp} style={{ display:"flex", justifyContent:"space-between",
                              alignItems:"center", marginBottom:5, gap:8 }}>
                              <span style={{ fontSize:11, color:"#666", flex:1 }}>{data.label}</span>
                              <span style={{ fontSize:11, fontWeight:700,
                                color: data.code === cl.koppen ? "#639922" : "#E24B4A",
                                background: data.code === cl.koppen ? "#EAF3DE" : "#FCEBEB",
                                padding:"2px 8px", borderRadius:20, flexShrink:0, fontFamily:"monospace" }}>
                                {data.code}
                              </span>
                            </div>
                          ))}
                        </>
                      )}
                      {cl.shift_direction && (
                        <div style={{ marginTop:10, padding:"8px 10px", background:"rgba(184,92,0,0.08)",
                          borderRadius:6, fontSize:11, color:"#7A3B00", lineHeight:1.6 }}>
                          {{
                            aridification:    "Drying trend — water availability will decline significantly.",
                            moistening:       "Wetting trend — fungal disease pressure will increase.",
                            continentalization:"More extreme temperatures in both directions.",
                            warming:          "Overall warming — new crop opportunities but higher pest pressure.",
                            tropicalization:  "Shift toward tropical conditions — frost risk decreasing.",
                            zone_shift:       "Climate zone boundary shifting — crop mix diversification recommended.",
                          }[cl.shift_direction] || "Climate conditions are changing at this location."}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* Future crop recommendations */}
        {result.future_crop_recommendations && result.future_crop_recommendations.length > 0 && (
          <div style={s.card}>
            <div style={s.cardLabel}>Long-term crop strategy</div>
            {result.future_crop_recommendations.map((rec, i) => (
              <div key={i} style={{ marginBottom:12, paddingBottom:12,
                borderBottom: i < result.future_crop_recommendations.length - 1
                  ? "0.5px solid #f0f0e8" : "none" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                  <span style={{ fontSize:10, fontWeight:700, color:"#fff",
                    background: rec.horizon.includes("Now") || rec.horizon.includes("season")
                      ? "#2d5a1b" : rec.horizon.includes("2–5") ? "#EF9F27" : "#1a6080",
                    padding:"2px 8px", borderRadius:20, flexShrink:0 }}>
                    {rec.horizon}
                  </span>
                </div>
                <div style={{ fontSize:13, fontWeight:600, color:"#1a2e1a", marginBottom:4 }}>
                  {rec.title}
                </div>
                <div style={{ fontSize:11, color:"#666", lineHeight:1.65 }}>
                  {rec.detail}
                </div>
              </div>
            ))}
            <div style={{ fontSize:10, color:"#aaa", marginTop:4 }}>
              Based on Beck et al. 2023 Köppen-Geiger projections (CMIP6)
            </div>
          </div>
        )}

        {/* Historical */}
        {result.historical && !result.historical.error && (
          <div style={{ ...s.card, borderLeft:"3px solid #ccc" }}>
            <div style={s.cardLabel}>One year ago — {result.historical.period}</div>
            <div style={{ display:"flex", gap:8, marginBottom:8 }}>
              {[{l:"NDVI",n:result.ndvi,th:result.historical.ndvi},{l:"NDMI",n:result.ndmi,th:result.historical.ndmi},{l:"NDRE",n:result.ndre,th:result.historical.ndre}].map(({l,n,th})=>{
                const diff=n!=null&&th!=null?round(n-th,3):null;
                return (
                  <div key={l} style={{ flex:1, background:"#fafaf8", borderRadius:8, padding:"8px", border:"0.5px solid #eee", textAlign:"center" }}>
                    <div style={{ fontSize:9, color:"#999", textTransform:"uppercase", letterSpacing:1, marginBottom:2 }}>{l}</div>
                    <div style={{ fontSize:15, fontWeight:700, color:"#666" }}>{th??"—"}</div>
                    {diff!=null && <div style={{ fontSize:10, fontWeight:600, marginTop:2, color:diff>0?"#639922":diff<0?"#E24B4A":"#888" }}>{diff>0?"+":diff<0?"-":"—"} {Math.abs(diff)}</div>}
                    <div style={{ fontSize:9, color:"#aaa", marginTop:1 }}>then / {n??"—"} now</div>
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize:11, color:"#555", padding:"6px 8px", background:"#f5f5f0", borderRadius:6 }}>
              <strong>{result.historical.health}</strong> this time last year ·{" "}
              {result.ndvi!=null&&result.historical.ndvi!=null
                ?result.ndvi>result.historical.ndvi?`+${round(result.ndvi-result.historical.ndvi,3)} NDVI vs ${result.historical.period}`
                :result.ndvi<result.historical.ndvi?`-${round(result.historical.ndvi-result.ndvi,3)} NDVI vs ${result.historical.period}`
                :`Unchanged vs ${result.historical.period}`:"No comparison"}
            </div>
          </div>
        )}

        {/* Data sources */}
        {result.dataSources && (
          <div style={{ padding:"8px 10px", borderRadius:8, border:"0.5px solid #e8e8e0", background:"#fafaf8" }}>
            <div style={{ fontSize:9, color:"#aaa", textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>{t("dataSources")}</div>
            {[["Satellite (Sentinel-2/ESA)",result.dataSources.satellite],["Weather (NASA POWER)",result.dataSources.weather],["Soil",result.dataSources.soil],["Climate normals",result.dataSources.climate],["Thermal",result.dataSources.thermal]].map(([l,v])=>(
              <div key={l} style={{ display:"flex", justifyContent:"space-between", fontSize:10, marginBottom:2 }}>
                <span style={{ color:"#999" }}>{l}</span>
                <span style={{ color:"#666", fontWeight:500 }}>{v}</span>
              </div>
            ))}
          </div>
        )}

        {/* Field Scout Checklist button */}
        {result.risks && result.risks.length > 0 && (
          <button
            onClick={() => { setScoutAnswers({}); setScoutOpen(true); }}
            style={{
              width:"100%", padding:"12px", marginTop:4,
              background:"#1a2e1a", color:"#fff", border:"none",
              borderRadius:8, fontSize:13, fontWeight:600,
              cursor:"pointer", letterSpacing:0.3,
            }}
          >
            Field Scout Checklist
          </button>
        )}
      </>}

      {/* ── FIELD SCOUT CHECKLIST MODAL ───────────────────────────────── */}
      {scoutOpen && result && (
        <div
          onClick={() => setScoutOpen(false)}
          style={{
            position:"fixed", inset:0, zIndex:2000,
            background:"rgba(0,0,0,0.55)", display:"flex",
            alignItems:"flex-end", justifyContent:"center",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width:"100%", maxWidth:520, background:"#fff",
              borderRadius:"16px 16px 0 0", padding:"0 0 32px 0",
              maxHeight:"85vh", display:"flex", flexDirection:"column",
            }}
          >
            {/* Header */}
            <div style={{ padding:"18px 20px 14px", borderBottom:"0.5px solid #efefef", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <div style={{ fontSize:15, fontWeight:700, color:"#1a2e1a" }}>Field Scout Checklist</div>
                <div style={{ fontSize:11, color:"#888", marginTop:2 }}>Did you observe these risks on your farm?</div>
              </div>
              <button onClick={() => setScoutOpen(false)} style={{ background:"none", border:"none", fontSize:20, color:"#aaa", cursor:"pointer", lineHeight:1 }}>×</button>
            </div>

            {/* Risk rows */}
            <div style={{ overflowY:"auto", flex:1 }}>
              {result.risks.map((r, i) => {
                const ans = scoutAnswers[r.name];
                return (
                  <div key={i} style={{
                    display:"flex", alignItems:"center", gap:12,
                    padding:"12px 20px",
                    borderBottom:"0.5px solid #f4f4f0",
                    background: ans === "yes" ? "#f0f7ea" : ans === "no" ? "#fdf5f5" : "#fff",
                  }}>
                    {/* Level dot */}
                    <div style={{ width:8, height:8, borderRadius:"50%", background:RISK_COLORS[r.level], flexShrink:0 }}/>
                    {/* Name + level */}
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:500, color:"#1a2e1a" }}>{r.name}</div>
                      <div style={{ fontSize:10, color:RISK_COLORS[r.level], fontWeight:600, marginTop:1 }}>{r.level}</div>
                    </div>
                    {/* Yes / No buttons */}
                    <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                      <button
                        onClick={() => setScoutAnswers(prev => ({ ...prev, [r.name]: "yes" }))}
                        style={{
                          padding:"6px 14px", borderRadius:20, fontSize:12, fontWeight:600,
                          border: ans === "yes" ? "none" : "1.5px solid #639922",
                          background: ans === "yes" ? "#639922" : "#fff",
                          color: ans === "yes" ? "#fff" : "#639922",
                          cursor:"pointer",
                        }}
                      >Yes</button>
                      <button
                        onClick={() => setScoutAnswers(prev => ({ ...prev, [r.name]: "no" }))}
                        style={{
                          padding:"6px 14px", borderRadius:20, fontSize:12, fontWeight:600,
                          border: ans === "no" ? "none" : "1.5px solid #E24B4A",
                          background: ans === "no" ? "#E24B4A" : "#fff",
                          color: ans === "no" ? "#fff" : "#E24B4A",
                          cursor:"pointer",
                        }}
                      >No</button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer — submit (no-op for now, wires to backend later) */}
            <div style={{ padding:"16px 20px 0", borderTop:"0.5px solid #efefef" }}>
              <div style={{ fontSize:11, color:"#aaa", marginBottom:10, textAlign:"center" }}>
                {Object.keys(scoutAnswers).length} of {result.risks.length} answered
              </div>
              <button
                onClick={() => {
                  console.log("Scout responses:", { field_coords: coordinates, crop, risks: scoutAnswers });
                  setScoutOpen(false);
                }}
                disabled={Object.keys(scoutAnswers).length === 0}
                style={{
                  width:"100%", padding:"12px", borderRadius:8, border:"none",
                  background: Object.keys(scoutAnswers).length === 0 ? "#e8e8e0" : "#1a2e1a",
                  color: Object.keys(scoutAnswers).length === 0 ? "#aaa" : "#fff",
                  fontSize:13, fontWeight:600, cursor: Object.keys(scoutAnswers).length === 0 ? "default" : "pointer",
                }}
              >
                Submit Observations
              </button>
            </div>
          </div>
        </div>
      )}
      </>}
    </>
  );

  // ── MAP ────────────────────────────────────────────────────────────────
  const mapEl = (
  <MapContainer center={mapCenter} zoom={mapZoom} key={`${mapCenter[0]},${mapCenter[1]},${mapZoom}`}
    style={{ height:'100%', width:'100%' }} zoomControl={!isMobile}>
    <TileLayer url={activeLayer.url} attribution={activeLayer.attribution} maxZoom={activeLayer.maxZoom}/>
    {activeLayer.overlay && <TileLayer url={activeLayer.overlay.url} opacity={activeLayer.overlay.opacity} attribution="OpenStreetMap"/>}
    {ndviTileUrl && <TileLayer url={ndviTileUrl} opacity={0.75} attribution="NDVI ESA"/>}
    {/* Active raster overlays */}
    {RASTER_LAYERS.filter(r => activeRasters[r.id]).map(r =>
      r.type === "wms" ? (
        <WMSTileLayer key={r.id} url={r.url} layers={r.wmsLayers}
          format="image/png" transparent={true} version="1.1.1"
          attribution={r.attribution} opacity={r.opacity || 0.7} maxZoom={r.maxZoom || 18}
          TIME={r.wmsTime || undefined} />
      ) : (
        <TileLayer key={r.id} url={r.url} attribution={r.attribution}
          opacity={r.opacity || 0.7} maxZoom={r.maxZoom || 18} maxNativeZoom={r.maxNativeZoom} />
      )
    )}

    {/* Desktop draw tool */}
    {!isMobile && (
      <FeatureGroup>
        <EditControl position="topright" onCreated={onFieldDrawn}
          draw={{ rectangle:false, circle:false, circlemarker:false, marker:false, polyline:false, polygon:true }}/>
      </FeatureGroup>
    )}

    {/* Completed polygon persisted on map after Done is tapped */}
    {drawnPolygon && drawnPolygon.length >= 3 && (
      <>
        <Polyline
          positions={[
            ...drawnPolygon.map(p => [p[1], p[0]]),
            [drawnPolygon[0][1], drawnPolygon[0][0]]
          ]}
          pathOptions={{ color: '#7AB648', weight: 2.5 }}
        />
        {drawnPolygon.map((pt, i) => (
          <CircleMarker
            key={i}
            center={[pt[1], pt[0]]}
            radius={5}
            pathOptions={{ color: '#2d5a1b', fillColor: '#7AB648', fillOpacity: 1, weight: 2 }}
          />
        ))}
      </>
    )}

    {/* Geolocation: auto-locate on mount, expose trigger and flyTo for search */}
    <LocateOnMap triggerRef={locateTriggerRef} flyRef={mapFlyRef} />

    {/* Mobile tap-to-draw */}
    <TouchPolygonDrawer
      onComplete={onTouchFieldComplete}
      onStartDrawing={() => setDrawnPolygon(null)}
      isMobile={isMobile}
    />
  </MapContainer>
);

  // ══════════════════════════════════════════════════════════════════════
  // MOBILE
  // ══════════════════════════════════════════════════════════════════════
  if (!embedMode && showDashboard) return (
    <>
      <Dashboard onBack={() => setShowDashboard(false)} onDisease={() => setShowDiseaseModal(true)} />
      {showDiseaseModal && <DiseaseModal crop={CROPS.find(c => c.toLowerCase() === crop) || crop} onClose={() => setShowDiseaseModal(false)} />}
    </>
  );

  if (isMobile) return (
    <div style={{ fontFamily:"system-ui, sans-serif", height:"100dvh", display:"flex", flexDirection:"column", background:"#000", overflow:"hidden", position:"relative" }}>

      {/* Header — hidden when embedded in the Dashboard iframe */}
      {!embedMode && <div style={{ background:"#1a2e1a", color:"#fff", padding:"10px 14px", display:"flex", alignItems:"center", gap:10, flexShrink:0, zIndex:100 }}>
        <span style={{ fontSize:16, fontWeight:700, flexShrink:0 }}>OpenFarm</span>
        <button onClick={() => setShowDiseaseModal(true)}
          style={{ padding:"5px 10px", fontSize:11, fontWeight:600, background:"#3a7a1e", color:"#fff", border:"1px solid #7AB648", borderRadius:20, cursor:"pointer", flexShrink:0, whiteSpace:"nowrap" }}>
          Disease
        </button>
        {!embedMode && (
          <button onClick={() => setShowDashboard(true)}
            style={{ padding:"5px 10px", fontSize:11, fontWeight:600, background:"rgba(255,255,255,0.1)", color:"rgba(255,255,255,0.85)", border:"1px solid rgba(255,255,255,0.25)", borderRadius:20, cursor:"pointer", flexShrink:0, whiteSpace:"nowrap" }}>
            Dashboard
          </button>
        )}
        <div style={{ flex:1, position:"relative" }}>
          <input
            placeholder={t("searchCityPlaceholder")}
            value={searchVal}
            onChange={e=>handleSearchInput(e.target.value)}
            onKeyDown={e=>{ if(e.key==="Enter"){ handleSearch(searchVal); } if(e.key==="Escape") setSearchSuggestions([]); }}
            onBlur={()=>setTimeout(()=>setSearchSuggestions([]),200)}
            style={{ width:"100%", boxSizing:"border-box", padding:"7px 12px", fontSize:13, border:"none", borderRadius:20, background:"rgba(255,255,255,0.15)", color:"#fff", outline:"none" }}/>
          {searchSuggestions.length > 0 && (
            <div style={{ position:"absolute", top:"calc(100% + 6px)", left:0, right:0, background:"#fff", borderRadius:10, boxShadow:"0 4px 20px rgba(0,0,0,0.2)", zIndex:600, overflow:"hidden" }}>
              {searchSuggestions.map((s,i)=>(
                <div key={i} onMouseDown={()=>flyToResult(s.lat, s.lon)}
                  style={{ padding:"9px 14px", fontSize:12, color:"#1a2e1a", borderBottom:i<searchSuggestions.length-1?"0.5px solid #f0f0e8":"none", cursor:"pointer", lineHeight:1.4 }}
                  onMouseEnter={e=>e.currentTarget.style.background="#f5f9f0"}
                  onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                  <div style={{ fontWeight:500 }}>{s.display_name.split(",")[0]}</div>
                  <div style={{ fontSize:10, color:"#999", marginTop:2 }}>{s.display_name.split(",").slice(1,3).join(",").trim()}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ position:"relative", flexShrink:0 }}>
          <button onClick={()=>setLangOpen(p=>!p)}
            style={{ background:"rgba(255,255,255,0.15)", border:"none", color:"#fff", borderRadius:20, padding:"7px 12px", fontSize:12, fontWeight:600, cursor:"pointer" }}>
            {LANGUAGES.find(l=>l.code===lang)?.label} 
          </button>
          {langOpen && (
            <div style={{ position:"absolute", right:0, top:"110%", background:"#fff", borderRadius:10, boxShadow:"0 4px 20px rgba(0,0,0,0.25)", zIndex:500, overflow:"hidden", minWidth:130 }}>
              {LANGUAGES.map(l=>(
                <button key={l.code} onClick={() => { setLang(l.code); setLangOpen(false); i18n.changeLanguage(l.code); }}
                  style={{ display:"block", width:"100%", padding:"10px 16px", textAlign:"left", border:"none", background:lang===l.code?"#EAF3DE":"#fff", fontSize:13, color:lang===l.code?"#2d5a1b":"#333", cursor:"pointer" }}>
                  {l.label} {l.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>}

      {/* Map full screen behind sheet */}
      <div style={{ position:"absolute", top:0, left:0, right:0, bottom:0, zIndex:0 }}>
        {mapEl}

        {/* Layer toggle + Rasters button — bottom LEFT, above sheet */}
        <div style={{ position:"absolute", bottom:`calc(${SNAPS[sheetSnap]} + 12px)`, left:12, zIndex:400, display:"flex", flexDirection:"column-reverse", gap:6, alignItems:"flex-start", transition:"bottom 0.3s ease" }}>
          <div style={{ display:"flex", background:"#fff", borderRadius:8, border:"0.5px solid #ddd", boxShadow:"0 2px 8px rgba(0,0,0,0.2)" }}>
            {Object.entries(MAP_LAYERS).map(([key,layer])=>(
              <button key={key} onClick={()=>setMapLayer(key)}
                style={{ padding:"7px 10px", fontSize:11, fontWeight:500, border:"none", background:mapLayer===key?"#1a2e1a":"transparent", color:mapLayer===key?"#fff":"#666", cursor:"pointer", whiteSpace:"nowrap" }}>
                {layer.label}
              </button>
            ))}
            <button onClick={() => setRasterPanelOpen(v => !v)}
              style={{ padding:"7px 10px", fontSize:11, fontWeight:500, border:"none", borderLeft:"0.5px solid #ddd",
                background: rasterPanelOpen || Object.values(activeRasters).some(Boolean) ? "#2d5a1b" : "transparent",
                color: rasterPanelOpen || Object.values(activeRasters).some(Boolean) ? "#fff" : "#666", cursor:"pointer" }}>
              Layers {Object.values(activeRasters).filter(Boolean).length > 0 ? `(${Object.values(activeRasters).filter(Boolean).length})` : ""}
            </button>
          </div>
          {/* Raster panel */}
          {rasterPanelOpen && (
            <div style={{ background:"#fff", borderRadius:10, border:"0.5px solid #ddd", boxShadow:"0 4px 16px rgba(0,0,0,0.15)", padding:"12px 14px", minWidth:230, maxHeight:300, overflowY:"auto" }}>
              <div style={{ fontSize:10, fontWeight:700, color:"#999", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Data Layers</div>
              {["Terrain","Soil","Vegetation","Climate"].map(cat => (
                <div key={cat} style={{ marginBottom:8 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:"#2d5a1b", marginBottom:4 }}>{cat}</div>
                  {RASTER_LAYERS.filter(r => r.category === cat).map(r => (
                    <label key={r.id} style={{ display:"flex", alignItems:"center", gap:8, fontSize:12, color:"#333", cursor:"pointer", marginBottom:4, userSelect:"none" }}>
                      <input type="checkbox" checked={!!activeRasters[r.id]}
                        onChange={e => setActiveRasters(prev => ({ ...prev, [r.id]: e.target.checked }))}
                        style={{ accentColor:"#2d5a1b", cursor:"pointer" }} />
                      {r.label}
                    </label>
                  ))}
                </div>
              ))}
              {Object.values(activeRasters).some(Boolean) && (
                <button onClick={() => setActiveRasters({})}
                  style={{ marginTop:4, fontSize:11, color:"#991b1b", background:"none", border:"none", cursor:"pointer", padding:0 }}>
                  Clear all layers
                </button>
              )}
            </div>
          )}
        </div>

        {/* Locate-me button — outside MapContainer so it's never clipped */}
        <div onClick={handleLocateMe} title={t("goToMyLocation")} style={{
          position:"absolute", bottom:100, right:12, zIndex:500,
          width:40, height:40, borderRadius:8,
          background:"#fff", boxShadow:"0 2px 8px rgba(0,0,0,0.35)",
          display:"flex", alignItems:"center", justifyContent:"center",
          cursor:"pointer", fontSize:20, border:"1px solid #ddd", userSelect:"none",
        }}>&#8982;</div>
      </div>

      {/* Raster legends — fixed to right edge, above everything */}
      {/* Mobile raster legends */}
      <RasterLegends activeRasters={activeRasters} apiBase={API_BASE_URL} style={{ position:"absolute", top:60, right:56, zIndex:500, maxHeight:"calc(100% - 80px)", overflowY:"auto" }} />

      {/* Bottom sheet — always rendered, height animates */}
      <div style={{
        position:"absolute", bottom:0, left:0, right:0, zIndex:300,
        height: SNAPS[sheetSnap],
        background:"#f5f5f0",
        borderRadius:"18px 18px 0 0",
        boxShadow:"0 -4px 24px rgba(0,0,0,0.15)",
        display:"flex", flexDirection:"column",
        transition:"height 0.28s cubic-bezier(0.4,0,0.2,1)",
        overflow:"hidden",
      }}>

        {/* ── Handle bar — ONLY this element responds to drag ── */}
        <div
          ref={handleBarRef}
          onTouchStart={onHandleTouchStart}
          onTouchEnd={onHandleTouchEnd}
          style={{ flexShrink:0, padding:"10px 14px 0", touchAction:"none", cursor:"grab", userSelect:"none" }}>
          {/* Visual pill */}
          <div style={{ display:"flex", justifyContent:"center", marginBottom:10 }}>
            <div style={{ width:40, height:4, borderRadius:2, background:"#ccc" }}/>
          </div>
        </div>

        {/* ── Status + CTA row ── */}
        <div style={{ flexShrink:0, padding:"0 14px 10px", borderBottom:"1px solid #e8e8e0", display:"flex", alignItems:"center", gap:8 }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:13, fontWeight:700, color:"#1a2e1a", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
              {loading ? t("analyzing")
               : result ? result.health_status + " · NDVI " + result.ndvi
               : coordinates ? t("fieldReady")
               : t("drawPrompt")}
            </div>
            {coordinates && !result && !loading && (
              <div style={{ fontSize:11, color:"#7a9a6a", marginTop:2 }}>
                {coordinates.length} {t("pointsDrawn")}
              </div>
            )}
          </div>

          {/* Primary CTA: Analyze — shown after field is drawn, before result */}
          {coordinates && !result && !loading && (
            <button
              onClick={async () => {
                setSheetSnap("half");
                // trigger the analyze button programmatically
                document.getElementById("mobile-analyze-btn")?.click();
              }}
              style={{ padding:"9px 16px", fontSize:13, fontWeight:700, background:"#2d5a1b", color:"#fff", border:"none", borderRadius:20, cursor:"pointer", flexShrink:0, whiteSpace:"nowrap" }}>
              {t("analyzeArrow")} ▶
            </button>
          )}

          {/* Print */}
          {result && (
            <button onClick={handlePrint}
              style={{ background:"#eee", border:"none", borderRadius:6, padding:"0 10px", height:30, fontSize:11, fontWeight:600, cursor:"pointer", color:"#444", flexShrink:0 }}>
              Print
            </button>
          )}
          {/* Expand/collapse */}
          <button onClick={()=>setSheetSnap(p=>p==="full"?"half":p==="half"?"bottom":"full")}
            style={{ background:"#e8e8e0", border:"none", borderRadius:"50%", width:30, height:30, fontSize:14, cursor:"pointer", color:"#666", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            {sheetSnap==="full"?"↓":"↑"}
          </button>
        </div>

        {/* ── Scrollable content — touch events here do NOT affect sheet snap ── */}
        <div id="openfarm-print-panel" style={{ flex:1, overflowY:"auto", padding:"8px 14px 32px", display:"flex", flexDirection:"column", gap:10, touchAction:"pan-y", overscrollBehavior:"contain" }}>
          {panel}
        </div>
      </div>

      {/* ── DISEASE DETECTOR MODAL (mobile) ── */}
      {showDiseaseModal && <DiseaseModal
        crop={CROPS.find(c => c.toLowerCase() === crop) || crop}
        onClose={() => setShowDiseaseModal(false)}
      />}
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════
  // DESKTOP
  // ══════════════════════════════════════════════════════════════════════
  if (!embedMode && showDashboard) return (
    <>
      <Dashboard onBack={() => setShowDashboard(false)} onDisease={() => setShowDiseaseModal(true)} />
      {showDiseaseModal && <DiseaseModal crop={CROPS.find(c => c.toLowerCase() === crop) || crop} onClose={() => setShowDiseaseModal(false)} />}
    </>
  );

  return (
    <div style={{ fontFamily:"system-ui, sans-serif", height:"100vh", display:"flex", flexDirection:"column", background:"#f5f5f0" }}>
      {!embedMode && <div style={{ background:"#1a2e1a", color:"#fff", padding:"10px 20px", display:"flex", alignItems:"center", gap:12, flexShrink:0 }}>
        <div style={{ fontSize:18, fontWeight:600 }}>OpenFarm</div>
        <div style={{ fontSize:13, opacity:0.6 }}>Farm Risk Intelligence</div>
        <button onClick={() => setShowDiseaseModal(true)}
          style={{ marginLeft:8, padding:"5px 14px", fontSize:12, fontWeight:600, background:"#3a7a1e", color:"#fff", border:"1px solid #7AB648", borderRadius:6, cursor:"pointer" }}>
          Disease Detector
        </button>
        <button onClick={() => setShowDashboard(true)}
          style={{ padding:"5px 14px", fontSize:12, fontWeight:600, background:"rgba(255,255,255,0.1)", color:"rgba(255,255,255,0.85)", border:"1px solid rgba(255,255,255,0.25)", borderRadius:6, cursor:"pointer" }}>
          Dashboard
        </button>
        <div style={{ marginLeft:"auto", display:"flex", gap:4, alignItems:"center" }}>
          <button onClick={handlePrint} disabled={!result}
            style={{ padding:"4px 12px", fontSize:11, fontWeight:600, background:"transparent", color: result ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.3)", border:`1px solid ${result ? "rgba(255,255,255,0.4)" : "rgba(255,255,255,0.15)"}`, borderRadius:5, cursor: result ? "pointer" : "default" }}>
            Print PDF
          </button>
          {LANGUAGES.map(l=>(
            <button key={l.code} onClick={() => { setLang(l.code); i18n.changeLanguage(l.code); }} title={l.name}
              style={{ padding:"4px 9px", fontSize:11, fontWeight:600, border:`1px solid ${lang===l.code?"#7AB648":"rgba(255,255,255,0.2)"}`, background:lang===l.code?"#7AB648":"transparent", color:lang===l.code?"#fff":"rgba(255,255,255,0.6)", borderRadius:5, cursor:"pointer" }}>
              {l.label}
            </button>
          ))}
        </div>
      </div>}

      <div style={{ display:"flex", flex:1, overflow:"hidden" }}>
        {/* Left panel */}
        <div id="openfarm-print-panel" style={{ width:panelWidth, minWidth:260, maxWidth:640, overflowY:"auto", padding:14, display:"flex", flexDirection:"column", gap:10, flexShrink:0 }}>
          {panel}
        </div>

        {/* Resize handle */}
        <div
          style={{ width:5, cursor:"col-resize", background:"#dde8d0", flexShrink:0, transition:"background 0.15s", zIndex:10 }}
          onMouseEnter={e => e.currentTarget.style.background="#7AB648"}
          onMouseLeave={e => { if (!dragRef.current) e.currentTarget.style.background="#dde8d0"; }}
          onMouseDown={e => {
            e.preventDefault();
            dragRef.current = { startX: e.clientX, startW: panelWidth };
            const handle = e.currentTarget;
            handle.style.background = "#2d5a1b";
            const onMove = mv => {
              const w = Math.min(640, Math.max(260, dragRef.current.startW + mv.clientX - dragRef.current.startX));
              setPanelWidth(w);
            };
            const onUp = () => {
              dragRef.current = null;
              handle.style.background = "#dde8d0";
              window.removeEventListener("mousemove", onMove);
              window.removeEventListener("mouseup", onUp);
            };
            window.addEventListener("mousemove", onMove);
            window.addEventListener("mouseup", onUp);
          }}
        />

        {/* Map */}
        <div style={{ flex:1, position:"relative" }}>
          {/* Desktop search */}
          <div style={{ position:"absolute", top:12, left:"50%", transform:"translateX(-50%)", zIndex:1000, display:"flex", gap:6, alignItems:"center", background:"#fff", padding:"6px 10px", borderRadius:10, boxShadow:"0 2px 10px rgba(0,0,0,0.15)", border:"0.5px solid #ddd" }}>
            <div style={{ position:"relative" }}>
              <input placeholder={t("searchCityPlaceholder")} value={searchVal}
                onChange={e=>handleSearchInput(e.target.value)}
                onKeyDown={e=>{ if(e.key==="Enter") handleSearch(searchVal); if(e.key==="Escape") setSearchSuggestions([]); }}
                onBlur={()=>setTimeout(()=>setSearchSuggestions([]),200)}
                style={{ padding:"6px 10px", fontSize:12, border:"0.5px solid #ddd", borderRadius:6, outline:"none", width:220, color:"#333" }}/>
              {searchSuggestions.length > 0 && (
                <div style={{ position:"absolute", top:"calc(100% + 6px)", left:0, right:0, background:"#fff", borderRadius:10, boxShadow:"0 4px 20px rgba(0,0,0,0.2)", zIndex:600, overflow:"hidden", minWidth:280 }}>
                  {searchSuggestions.map((s,i)=>(
                    <div key={i} onMouseDown={()=>flyToResult(s.lat, s.lon)}
                      style={{ padding:"9px 14px", fontSize:12, color:"#1a2e1a", borderBottom:i<searchSuggestions.length-1?"0.5px solid #f0f0e8":"none", cursor:"pointer", lineHeight:1.4 }}
                      onMouseEnter={e=>e.currentTarget.style.background="#f5f9f0"}
                      onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                      <div style={{ fontWeight:500 }}>{s.display_name.split(",")[0]}</div>
                      <div style={{ fontSize:10, color:"#999", marginTop:2 }}>{s.display_name.split(",").slice(1,3).join(",").trim()}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button onClick={()=>handleSearch(searchVal)} style={{ padding:"6px 12px", fontSize:12, fontWeight:600, background:"#1a2e1a", color:"#fff", border:"none", borderRadius:6, cursor:"pointer" }}>Go</button>
          </div>

          {/* Layer toggle + Rasters — desktop bottom center */}
          <div style={{ position:"absolute", bottom:24, left:"50%", transform:"translateX(-50%)", zIndex:1000, display:"flex", flexDirection:"column", alignItems:"center", gap:8 }}>
            {rasterPanelOpen && (
              <div style={{ background:"#fff", borderRadius:10, border:"0.5px solid #ddd", boxShadow:"0 4px 16px rgba(0,0,0,0.15)", padding:"12px 16px", display:"flex", gap:24 }}>
                {["Terrain","Soil","Vegetation","Climate"].map(cat => (
                  <div key={cat}>
                    <div style={{ fontSize:10, fontWeight:700, color:"#2d5a1b", marginBottom:6, textTransform:"uppercase" }}>{cat}</div>
                    {RASTER_LAYERS.filter(r => r.category === cat).map(r => (
                      <label key={r.id} style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"#333", cursor:"pointer", marginBottom:4, userSelect:"none", whiteSpace:"nowrap" }}>
                        <input type="checkbox" checked={!!activeRasters[r.id]}
                          onChange={e => setActiveRasters(prev => ({ ...prev, [r.id]: e.target.checked }))}
                          style={{ accentColor:"#2d5a1b", cursor:"pointer" }} />
                        {r.label}
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            )}
            <div style={{ display:"flex", background:"#fff", borderRadius:8, border:"0.5px solid #ddd", overflow:"hidden", boxShadow:"0 2px 8px rgba(0,0,0,0.15)" }}>
              {Object.entries(MAP_LAYERS).map(([key,layer])=>(
                <button key={key} onClick={()=>setMapLayer(key)}
                  style={{ padding:"7px 16px", fontSize:12, fontWeight:500, border:"none", background:mapLayer===key?"#1a2e1a":"transparent", color:mapLayer===key?"#fff":"#666", cursor:"pointer" }}>
                  {layer.label}
                </button>
              ))}
              <button onClick={() => setRasterPanelOpen(v => !v)}
                style={{ padding:"7px 16px", fontSize:12, fontWeight:500, border:"none", borderLeft:"0.5px solid #ddd",
                  background: rasterPanelOpen || Object.values(activeRasters).some(Boolean) ? "#2d5a1b" : "transparent",
                  color: rasterPanelOpen || Object.values(activeRasters).some(Boolean) ? "#fff" : "#666", cursor:"pointer" }}>
                Layers {Object.values(activeRasters).filter(Boolean).length > 0 ? `(${Object.values(activeRasters).filter(Boolean).length})` : ""}
              </button>
            </div>
          </div>

          {/* Raster legends — desktop right edge, clear of draw tools */}
          <RasterLegends activeRasters={activeRasters} apiBase={API_BASE_URL} style={{ position:"absolute", top:60, right:56, zIndex:1000, maxHeight:"calc(100% - 120px)", overflowY:"auto" }} />

          {/* Loading overlay on desktop map */}
          {loading && (
            <div style={{ position:'absolute', inset:0, zIndex:500, background:'rgba(10,26,10,0.55)',
              display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:16 }}>
              <div style={{ width:48, height:48, border:'4px solid rgba(255,255,255,0.2)',
                borderTop:'4px solid #7AB648', borderRadius:'50%',
                animation:'spin 0.9s linear infinite' }}/>
              <div style={{ color:'#fff', fontSize:15, fontWeight:600, letterSpacing:0.3 }}>{t("analyzing")}</div>
              {serverBusy && (
                <div style={{ background:"rgba(255,200,0,0.15)", border:"1px solid rgba(255,200,0,0.4)",
                  borderRadius:8, padding:"8px 14px", maxWidth:280, textAlign:"center" }}>
                  <div style={{ color:"#ffd700", fontSize:12, fontWeight:600 }}>Server is busy</div>
                  <div style={{ color:"rgba(255,255,255,0.75)", fontSize:11, marginTop:3 }}>
                    {queueDepth > 0
                      ? `${queueDepth} request${queueDepth > 1 ? "s" : ""} ahead of yours — please hold`
                      : "Processing — your analysis is next in line"}
                  </div>
                </div>
              )}
              {coordinates && (
                <div style={{ color:'rgba(255,255,255,0.6)', fontSize:12 }}>
                  {coordinates.length}-point field boundary
                </div>
              )}
            </div>
          )}
          {mapEl}

          {/* Locate-me button — outside MapContainer so it's never clipped */}
          <div onClick={handleLocateMe} title={t("goToMyLocation")} style={{
            position:"absolute", bottom:90, right:12, zIndex:500,
            width:40, height:40, borderRadius:8,
            background:"#fff", boxShadow:"0 2px 8px rgba(0,0,0,0.35)",
            display:"flex", alignItems:"center", justifyContent:"center",
            cursor:"pointer", fontSize:20, border:"1px solid #ddd", userSelect:"none",
          }}>&#8982;</div>
        </div>
      </div>

      {/* ── DISEASE DETECTOR MODAL (desktop) ── */}
      {showDiseaseModal && <DiseaseModal
        crop={CROPS.find(c => c.toLowerCase() === crop) || crop}
        onClose={() => setShowDiseaseModal(false)}
      />}
    </div>
  );
}

// inject spinner keyframe
if (typeof document !== 'undefined' && !document.getElementById('openfarm-spin')) {
  const st = document.createElement('style');
  st.id = 'openfarm-spin';
  st.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(st);
}

const s = {
  card:         { background:"#fff", borderRadius:10, padding:"12px 14px", border:"0.5px solid #e0e0d8", flexShrink:0 },
  cardLabel:    { fontSize:11, fontWeight:600, color:"#888", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:8 },
  hint:         { fontSize:13, color:"#666", margin:0 },
  chip:         { marginTop:8, fontSize:12, color:"#3B6D11", background:"#EAF3DE", padding:"4px 10px", borderRadius:20, display:"inline-block" },
  cropSelect:   { width:"100%", padding:"9px 12px", fontSize:13, borderRadius:8, border:"0.5px solid #ddd", background:"#fafaf8", cursor:"pointer", color:"#1a2e1a" },
  analyzeBtn:   { background:"#2d5a1b", color:"#fff", border:"none", borderRadius:8, padding:"12px 16px", fontSize:14, fontWeight:500, cursor:"pointer", width:"100%", flexShrink:0 },
  smallBtn:     { padding:"5px 14px", fontSize:12, fontWeight:500, border:"0.5px solid #ddd", background:"#fafaf8", borderRadius:6, cursor:"pointer", color:"#666" },
  smallBtnActive:{ background:"#1a2e1a", color:"#fff", border:"0.5px solid #1a2e1a" },
  error:        { fontSize:12, color:"#E24B4A", padding:"8px 12px", background:"#FCEBEB", borderRadius:6 },
  lbl:          { fontSize:10, color:"#888", marginTop:2 },
};